import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DOMPurify from 'dompurify';
import EventTypeAdmin from '../components/events/EventTypeAdmin';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Users, Layers, Image, Plus, Trash2, Edit3, ChevronRight, Search,
    UserPlus, Shield, FolderOpen, FileText, Video, Upload, X, Check,
    Eye, Globe, Sparkles, AlertTriangle, BarChart3, Building2, Target,
    CheckSquare, Presentation, Zap, ToggleLeft, ToggleRight, Mic, ChevronDown,
    FileSignature, Mail, UserCog, KeyRound, UserX, UserCheck, Copy, Link2,
    Loader2, MailCheck, Calendar, Briefcase, LayoutGrid, ArrowUp, ArrowDown, Clock,
    TrendingUp, DollarSign, Tag, Package, CalendarDays, MoreHorizontal, Ban, ShieldCheck,
    Lock, Unlock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import RepProfilePanel from '../components/RepProfilePanel';
import CoachesTab from '../components/admin/CoachesTab';
import LeadPipelineTab from '../components/admin/LeadPipelineTab';
import UserProfileMdPanel from '../components/UserProfileMdPanel';
import CustomFieldsTab from '../components/CustomFieldsTab';
import TagManagementTab from '../components/TagManagementTab';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import RichTextEditor from '../components/RichTextEditor';
import SignatureEditor from '../components/SignatureEditor';
import PillarRoleEditor from '../components/admin/PillarRoleEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// ─── Team Members Tab ───────────────────────────────────────────────────────

const roleColors = { admin: 'bg-stone-800 text-white', manager: 'bg-stone-200 text-stone-700', sales_rep: 'bg-stone-100 text-stone-600' };

function TeamMembersTab() {
    const { getAuthHeader } = useAuth();
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterGroup, setFilterGroup] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState({ name: '', email: '', password: '', role: 'sales_rep', group_ids: [] });
    const [adding, setAdding] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [showResetPw, setShowResetPw] = useState(false);
    const [resetPwUser, setResetPwUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [showBulk, setShowBulk] = useState(false);
    const [bulkEmails, setBulkEmails] = useState('');
    const [bulkRole, setBulkRole] = useState('sales_rep');
    const [bulkPassword, setBulkPassword] = useState('');
    const [bulkResult, setBulkResult] = useState(null);
    const [showRepProfile, setShowRepProfile] = useState(false);
    const [repProfileUser, setRepProfileUser] = useState(null);
    const [showProfileMd, setShowProfileMd] = useState(false);
    const [profileMdUser, setProfileMdUser] = useState(null);
    const [actionMenuOpen, setActionMenuOpen] = useState(null); // user_id of open menu
    const [bulking, setBulking] = useState(false);
    const [showInviteLink, setShowInviteLink] = useState(false);
    const [inviteLink, setInviteLink] = useState(null);
    const [inviteDomains, setInviteDomains] = useState('');
    const [inviteRole, setInviteRole] = useState('sales_rep');
    const [inviteEmails, setInviteEmails] = useState('');
    const [sendingInvites, setSendingInvites] = useState(false);
    const [inviteSendResult, setInviteSendResult] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (filterRole) params.set('role', filterRole);
            if (filterStatus) params.set('status', filterStatus);
            if (filterGroup) params.set('group_id', filterGroup);
            const [usersRes, groupsRes] = await Promise.all([
                axios.get(`${API}/admin/users?${params}`, { headers: getAuthHeader() }),
                axios.get(`${API}/admin/user-groups`, { headers: getAuthHeader() }),
            ]);
            setUsers(usersRes.data);
            setGroups(groupsRes.data);
        } catch { toast.error('Failed to load team'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [search, filterRole, filterStatus, filterGroup]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!addForm.email || !addForm.password) return toast.error('Email and password required');
        setAdding(true);
        try {
            await axios.post(`${API}/admin/users`, addForm, { headers: getAuthHeader() });
            toast.success('User created'); setShowAdd(false);
            setAddForm({ name: '', email: '', password: '', role: 'sales_rep', group_ids: [] }); load();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
        finally { setAdding(false); }
    };
    const openEdit = (u) => { setEditUser(u); setEditForm({ name: u.name, role: u.role, is_admin: u.is_admin, group_ids: u.groups?.map(g => g.group_id) || [], picture: u.picture || '', org_role: u.org_role || (u.is_admin ? 'org_admin' : 'member'), pillar_roles: u.pillar_roles || {}, is_agent_developer: u.is_agent_developer || false }); setShowEdit(true); };
    const handleEdit = async () => {
        try {
            const headers = getAuthHeader();
            // Save basic fields
            await axios.put(`${API}/admin/users/${editUser.user_id}`, { name: editForm.name, group_ids: editForm.group_ids, picture: editForm.picture, is_agent_developer: editForm.is_agent_developer }, { headers });
            // Save org role + pillar roles
            await axios.patch(`${API}/admin/users/${editUser.user_id}/org-role`, { org_role: editForm.org_role }, { headers });
            if (editForm.org_role === 'member' && editForm.pillar_roles) {
                await axios.patch(`${API}/admin/users/${editUser.user_id}/pillar-roles`, { pillar_roles: editForm.pillar_roles }, { headers });
            }
            toast.success('User updated'); setShowEdit(false); load();
        }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };
    const handleViewAs = async (u) => {
        try {
            const res = await axios.post(`${API}/admin/view-as/${u.user_id}`, {}, { headers: getAuthHeader() });
            const { access_token, viewed_user } = res.data;
            const viewUrl = `${window.location.origin}/dashboard?impersonate_token=${access_token}&impersonate_name=${encodeURIComponent('View As: ' + (viewed_user.name || viewed_user.email))}`;
            window.open(viewUrl, '_blank');
            toast.success(`Viewing as ${viewed_user.name || viewed_user.email} in a new tab`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to view as user'); }
    };
    const toggleActive = async (u) => {
        try { const res = await axios.put(`${API}/admin/users/${u.user_id}/toggle-active`, {}, { headers: getAuthHeader() }); toast.success(res.data.message); load(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };
    const handleResetPw = async () => {
        if (!newPassword || newPassword.length < 8) return toast.error('Password must be at least 8 characters');
        try { await axios.put(`${API}/admin/users/${resetPwUser.user_id}/reset-password`, { new_password: newPassword }, { headers: getAuthHeader() }); toast.success('Password reset'); setShowResetPw(false); setNewPassword(''); }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };
    const handleBulk = async () => {
        const emails = bulkEmails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
        if (!emails.length) return toast.error('No emails entered');
        if (!bulkPassword || bulkPassword.length < 8) return toast.error('Min 8 characters');
        setBulking(true);
        try { const res = await axios.post(`${API}/admin/invite/bulk`, { emails, role: bulkRole, password: bulkPassword }, { headers: getAuthHeader() }); setBulkResult(res.data); load(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
        finally { setBulking(false); }
    };
    const loadInviteLink = async () => {
        try { const res = await axios.get(`${API}/admin/invite-link`, { headers: getAuthHeader() }); setInviteLink(res.data); if (res.data?.allowed_domains) setInviteDomains(res.data.allowed_domains.join(', ')); if (res.data?.default_role) setInviteRole(res.data.default_role); } catch {}
    };
    const createInviteLink = async () => {
        try { const domains = inviteDomains.split(/[,;]+/).map(d => d.trim()).filter(Boolean); const res = await axios.post(`${API}/admin/invite-link`, { allowed_domains: domains, default_role: inviteRole }, { headers: getAuthHeader() }); setInviteLink(res.data); toast.success('Invite link created'); }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };
    const toggleInviteLink = async () => {
        try { const res = await axios.put(`${API}/admin/invite-link/toggle`, {}, { headers: getAuthHeader() }); setInviteLink(prev => ({...prev, is_active: res.data.is_active})); toast.success(res.data.is_active ? 'Link activated' : 'Link deactivated'); }
        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };
    const getJoinUrl = () => inviteLink?.link_token ? `${window.location.origin}/join/${inviteLink.link_token}` : inviteLink?.registration_url || '';
    const copyLink = () => { const url = getJoinUrl(); if (url) { navigator.clipboard.writeText(url); toast.success('Link copied'); } };

    const sendInviteEmails = async () => {
        const emails = inviteEmails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
        if (!emails.length) { toast.error('Enter at least one email address'); return; }
        setSendingInvites(true);
        setInviteSendResult(null);
        try {
            const res = await axios.post(`${API}/admin/invite-link/send`, { emails }, { headers: getAuthHeader() });
            setInviteSendResult(res.data);
            if (res.data.sent_count > 0) toast.success(`${res.data.sent_count} invitation(s) sent`);
            if (res.data.failed?.length > 0) toast.error(`${res.data.failed.length} failed`);
            setInviteEmails('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send invitations');
        } finally {
            setSendingInvites(false);
        }
    };

    return (
        <>
            <div className="flex items-center gap-3 flex-wrap mb-4">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search team members..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" data-testid="team-search-input" />
                </div>
                <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="team-role-filter">
                    <option value="">All Roles</option><option value="admin">Admin</option><option value="manager">Manager</option><option value="sales_rep">Sales Rep</option>
                </select>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="team-status-filter">
                    <option value="">All</option><option value="active">Active</option><option value="inactive">Suspended</option>
                </select>
                <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)} className="px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="team-group-filter">
                    <option value="">All Groups</option>
                    {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
                </select>
                <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-user-btn"><UserPlus className="w-4 h-4 mr-1" /> Add User</Button>
                <Button size="sm" variant="outline" onClick={() => setShowBulk(true)} data-testid="bulk-invite-btn"><Users className="w-4 h-4 mr-1" /> Bulk Invite</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowInviteLink(true); loadInviteLink(); }} data-testid="invite-link-btn"><Link2 className="w-4 h-4 mr-1" /> Invite Link</Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-500 animate-spin" /></div>
            ) : users.length === 0 ? (
                <div className="text-center py-12"><Users className="w-12 h-12 text-stone-200 mx-auto mb-3" /><p className="text-stone-500">No team members found</p></div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full" data-testid="team-table">
                        <thead><tr className="text-left text-xs font-medium text-stone-500 uppercase tracking-wider border-b border-stone-200">
                            <th className="px-4 py-3">Member</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Integrations</th><th className="px-4 py-3">Groups</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3">Last Login</th><th className="px-4 py-3 text-right">Actions</th>
                        </tr></thead>
                        <tbody className="divide-y divide-stone-100">
                            {users.map(u => (
                                <tr key={u.user_id} className="hover:bg-stone-50 transition-colors" data-testid={`team-row-${u.user_id}`}>
                                    <td className="px-4 py-3"><div className="flex items-center gap-3"><div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${u.is_active ? 'bg-stone-200 text-stone-700' : 'bg-stone-100 text-stone-400'}`}>{u.name?.charAt(0)?.toUpperCase() || '?'}</div><div><div className="text-sm font-medium text-stone-900">{u.name || 'Unnamed'}</div><div className="text-xs text-stone-400">{u.email}</div></div></div></td>
                                    <td className="px-4 py-3">{u.org_role === 'org_admin' || u.is_admin ? (
                                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-stone-900 text-white">Org Admin</span>
                                    ) : u.pillar_roles && Object.keys(u.pillar_roles).length > 0 ? (
                                        <div className="flex flex-wrap gap-1">{Object.entries(u.pillar_roles).slice(0, 3).map(([pid, pr]) => (
                                            <span key={pid} className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ${pr.read_only ? 'bg-amber-50 text-amber-700' : roleColors[pr.role] || roleColors.sales_rep}`}>{pid.replace('custom_mod_', '').slice(0, 5)}: {pr.role}{pr.read_only ? ' (RO)' : ''}</span>
                                        ))}{Object.keys(u.pillar_roles).length > 3 && <span className="text-[10px] text-stone-400">+{Object.keys(u.pillar_roles).length - 3}</span>}</div>
                                    ) : (
                                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${roleColors[u.role] || roleColors.sales_rep}`}>{u.role?.replace('_', ' ')}</span>
                                    )}</td>
                                    <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}><span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />{u.is_active ? 'Active' : 'Suspended'}</span></td>
                                    <td className="px-4 py-3"><div className="flex items-center gap-2">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${u.gmail_connected ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-400'}`} title={u.gmail_connected ? `Gmail: ${u.gmail_email}` : 'Not connected'}><MailCheck className="w-3 h-3" /> Email</span>
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${u.calendar_connected ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-400'}`}><Calendar className="w-3 h-3" /> Cal</span>
                                    </div></td>
                                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{u.groups?.length > 0 ? u.groups.map(g => <span key={g.group_id} className="inline-flex px-1.5 py-0.5 text-xs bg-stone-100 text-stone-600 rounded">{g.name}</span>) : <span className="text-xs text-stone-400">None</span>}</div></td>
                                    <td className="px-4 py-3 text-xs text-stone-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                                    <td className="px-4 py-3 text-xs text-stone-400">{u.last_login ? `${new Date(u.last_login).toLocaleDateString()} ${new Date(u.last_login).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Never'}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="relative inline-block">
                                            <button
                                                onClick={() => setActionMenuOpen(actionMenuOpen === u.user_id ? null : u.user_id)}
                                                className="p-1.5 text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-colors"
                                            >
                                                <MoreHorizontal className="w-4 h-4" />
                                            </button>
                                            {actionMenuOpen === u.user_id && (
                                                <>
                                                    <div className="fixed inset-0 z-40" onClick={() => setActionMenuOpen(null)} />
                                                    <div className="absolute right-0 top-full mt-1 z-50 w-48 bg-white rounded-lg border border-stone-200 shadow-lg py-1">
                                                        <button onClick={() => { openEdit(u); setActionMenuOpen(null); }}
                                                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 text-left">
                                                            <Edit3 className="w-3.5 h-3.5 text-stone-400" /> Edit User
                                                        </button>
                                                        <button onClick={() => { handleViewAs(u); setActionMenuOpen(null); }}
                                                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 text-left">
                                                            <Eye className="w-3.5 h-3.5 text-stone-400" /> View As User
                                                        </button>
                                                        <button onClick={() => { setRepProfileUser(u); setShowRepProfile(true); setActionMenuOpen(null); }}
                                                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 text-left">
                                                            <Briefcase className="w-3.5 h-3.5 text-stone-400" /> Rep Profile
                                                        </button>
                                                        <button onClick={() => { setProfileMdUser(u); setShowProfileMd(true); setActionMenuOpen(null); }}
                                                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 text-left">
                                                            <FileText className="w-3.5 h-3.5 text-stone-400" /> AI Profile
                                                        </button>
                                                        <div className="border-t border-stone-100 my-1" />
                                                        <button onClick={() => { setResetPwUser(u); setShowResetPw(true); setActionMenuOpen(null); }}
                                                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 text-left">
                                                            <KeyRound className="w-3.5 h-3.5 text-stone-400" /> Reset Password
                                                        </button>
                                                        <button onClick={() => { toggleActive(u); setActionMenuOpen(null); }}
                                                            className={`flex items-center gap-2.5 w-full px-3 py-2 text-sm text-left ${u.is_active ? 'text-red-600 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'}`}>
                                                            {u.is_active ? <><Ban className="w-3.5 h-3.5" /> Suspend User</> : <><ShieldCheck className="w-3.5 h-3.5" /> Activate User</>}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add User Modal */}
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Add Team Member</DialogTitle><DialogDescription>Create a new user in your organization</DialogDescription></DialogHeader>
                    <form onSubmit={handleAdd} className="space-y-4" data-testid="add-user-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Name</Label><Input value={addForm.name} onChange={e => setAddForm(f => ({...f, name: e.target.value}))} placeholder="John Smith" className="mt-1" data-testid="add-user-name" /></div>
                            <div><Label>Role</Label><select value={addForm.role} onChange={e => setAddForm(f => ({...f, role: e.target.value}))} className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 rounded-lg" data-testid="add-user-role"><option value="sales_rep">Sales Rep</option><option value="manager">Manager</option><option value="admin">Admin</option></select></div>
                        </div>
                        <div><Label>Email *</Label><Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({...f, email: e.target.value}))} placeholder="user@company.com" className="mt-1" data-testid="add-user-email" /></div>
                        <div><Label>Password *</Label><Input type="password" value={addForm.password} onChange={e => setAddForm(f => ({...f, password: e.target.value}))} placeholder="Min 8 characters" className="mt-1" data-testid="add-user-password" /></div>
                        {groups.length > 0 && (<div><Label>Groups</Label><div className="flex flex-wrap gap-2 mt-1">{groups.map(g => (<label key={g.group_id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border cursor-pointer transition-colors ${addForm.group_ids.includes(g.group_id) ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'}`}><input type="checkbox" className="hidden" checked={addForm.group_ids.includes(g.group_id)} onChange={e => setAddForm(f => ({...f, group_ids: e.target.checked ? [...f.group_ids, g.group_id] : f.group_ids.filter(id => id !== g.group_id)}))} />{g.name}</label>))}</div></div>)}
                        <DialogFooter><Button variant="outline" type="button" onClick={() => setShowAdd(false)}>Cancel</Button><Button type="submit" disabled={adding} className="bg-stone-900 hover:bg-stone-800" data-testid="add-user-submit">{adding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />} Add User</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit User Modal */}
            <Dialog open={showEdit} onOpenChange={setShowEdit}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit Team Member</DialogTitle><DialogDescription>{editUser?.email}</DialogDescription></DialogHeader>
                    <div className="space-y-4" data-testid="edit-user-form">
                        <div>
                            <Label>Name</Label>
                            <Input value={editForm.name || ''} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} className="mt-1" />
                        </div>
                        {/* Pillar Role Assignment */}
                        <PillarRoleEditor
                            orgRole={editForm.org_role}
                            pillarRoles={editForm.pillar_roles}
                            isAgentDeveloper={editForm.is_agent_developer}
                            onChange={(orgRole, pillarRoles, isAgentDeveloper) => setEditForm(f => ({...f, org_role: orgRole, pillar_roles: pillarRoles, is_agent_developer: isAgentDeveloper}))}
                        />
                        {groups.length > 0 && (<div><Label>Groups</Label><div className="flex flex-wrap gap-2 mt-1">{groups.map(g => (<label key={g.group_id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border cursor-pointer transition-colors ${editForm.group_ids?.includes(g.group_id) ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'}`}><input type="checkbox" className="hidden" checked={editForm.group_ids?.includes(g.group_id) || false} onChange={e => setEditForm(f => ({...f, group_ids: e.target.checked ? [...(f.group_ids||[]), g.group_id] : (f.group_ids||[]).filter(id => id !== g.group_id)}))} />{g.name}</label>))}</div></div>)}
                        <div>
                            <label className="text-xs font-medium text-stone-600 mb-1 block">Profile Photo</label>
                            <div className="flex items-center gap-3">
                                {editForm.picture ? (
                                    <img src={editForm.picture} className="w-10 h-10 rounded-full object-cover" alt="" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-400 text-xs font-medium">
                                        {editForm.name?.charAt(0) || '?'}
                                    </div>
                                )}
                                <input className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg" value={editForm.picture || ''} onChange={e => setEditForm(f => ({...f, picture: e.target.value}))} placeholder="Profile photo URL" />
                            </div>
                        </div>
                        <DialogFooter><Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button><Button onClick={handleEdit} className="bg-stone-900 hover:bg-stone-800" data-testid="edit-user-submit">Save Changes</Button></DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Reset Password Modal */}
            <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
                <DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>{resetPwUser?.email}</DialogDescription></DialogHeader>
                    <div><Label>New Password *</Label><Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" className="mt-1" data-testid="reset-pw-input" /></div>
                    <DialogFooter><Button variant="outline" onClick={() => { setShowResetPw(false); setNewPassword(''); }}>Cancel</Button><Button onClick={handleResetPw} className="bg-stone-900 hover:bg-stone-800" data-testid="reset-pw-submit"><KeyRound className="w-4 h-4 mr-1" /> Reset</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Invite Modal */}
            <Dialog open={showBulk} onOpenChange={v => { setShowBulk(v); if (!v) setBulkResult(null); }}>
                <DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle>Bulk Invite Users</DialogTitle><DialogDescription>Add multiple team members at once (max 50)</DialogDescription></DialogHeader>
                    {!bulkResult ? (
                        <div className="space-y-4" data-testid="bulk-invite-form">
                            <div><Label>Email Addresses</Label><Textarea value={bulkEmails} onChange={e => setBulkEmails(e.target.value)} rows={5} placeholder={"Paste emails separated by commas or new lines\njohn@company.com\njane@company.com"} className="mt-1 font-mono text-xs" data-testid="bulk-emails" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Role</Label><select value={bulkRole} onChange={e => setBulkRole(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 rounded-lg"><option value="sales_rep">Sales Rep</option><option value="manager">Manager</option><option value="admin">Admin</option></select></div>
                                <div><Label>Initial Password *</Label><Input type="password" value={bulkPassword} onChange={e => setBulkPassword(e.target.value)} placeholder="Min 8 chars" className="mt-1" data-testid="bulk-password" /></div>
                            </div>
                            <DialogFooter><Button variant="outline" onClick={() => setShowBulk(false)}>Cancel</Button><Button onClick={handleBulk} disabled={bulking} className="bg-stone-900 hover:bg-stone-800" data-testid="bulk-submit">{bulking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Users className="w-4 h-4 mr-1" />} Invite All</Button></DialogFooter>
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="bulk-result">
                            <div className="grid grid-cols-2 gap-3"><div className="bg-emerald-50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-emerald-700">{bulkResult.total_created}</div><div className="text-xs text-emerald-600">Created</div></div><div className="bg-amber-50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-amber-700">{bulkResult.total_skipped}</div><div className="text-xs text-amber-600">Skipped</div></div></div>
                            {bulkResult.skipped?.length > 0 && (<div><Label className="text-xs text-stone-500">Skipped emails</Label><div className="mt-1 max-h-32 overflow-y-auto space-y-1">{bulkResult.skipped.map((s, i) => (<div key={i} className="flex items-center justify-between text-xs bg-stone-50 rounded px-2 py-1.5"><span className="text-stone-600">{s.email}</span><span className="text-amber-600">{s.reason}</span></div>))}</div></div>)}
                            <DialogFooter><Button onClick={() => { setShowBulk(false); setBulkResult(null); setBulkEmails(''); setBulkPassword(''); }} className="bg-stone-900 hover:bg-stone-800">Done</Button></DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Invite Link Modal */}
            <Dialog open={showInviteLink} onOpenChange={(open) => { setShowInviteLink(open); if (!open) setInviteSendResult(null); }}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Invite Team Members</DialogTitle>
                        <DialogDescription>Share a self-registration link or send email invitations directly</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5" data-testid="invite-link-form">
                        {/* Role Selector - Prominent */}
                        <div>
                            <Label className="text-sm font-medium mb-2 block">Default Role for New Members</Label>
                            <div className="flex gap-2" data-testid="invite-role-selector">
                                {[
                                    { value: 'sales_rep', label: 'Sales Rep', icon: Users },
                                    { value: 'manager', label: 'Manager', icon: Briefcase },
                                    { value: 'admin', label: 'Admin', icon: Shield },
                                ].map((role) => {
                                    const Icon = role.icon;
                                    return (
                                        <button
                                            key={role.value}
                                            onClick={() => setInviteRole(role.value)}
                                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-all ${
                                                inviteRole === role.value
                                                    ? 'border-stone-900 bg-stone-900 text-white'
                                                    : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                                            }`}
                                            data-testid={`invite-role-${role.value}`}
                                        >
                                            <Icon className="w-4 h-4" />
                                            {role.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Self-Registration Link */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Self-Registration Link</Label>
                            {inviteLink ? (<>
                                <div className="bg-stone-50 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${inviteLink.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                            {inviteLink.is_active ? 'Active' : 'Disabled'}
                                        </span>
                                        <span className="text-xs text-stone-400">{inviteLink.uses || 0} signups</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-xs font-mono bg-white border border-stone-200 rounded px-2 py-1.5 truncate" data-testid="invite-link-url">{getJoinUrl()}</code>
                                        <button onClick={copyLink} className="p-2 bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition-colors" data-testid="copy-invite-link">
                                            <Copy className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={toggleInviteLink} data-testid="toggle-invite-link">{inviteLink.is_active ? 'Disable Link' : 'Enable Link'}</Button>
                                    <Button variant="outline" size="sm" onClick={createInviteLink}>Regenerate</Button>
                                </div>
                            </>) : (
                                <Button onClick={createInviteLink} className="bg-stone-900 hover:bg-stone-800 w-full" data-testid="create-invite-link">
                                    <Link2 className="w-4 h-4 mr-1" /> Generate Invite Link
                                </Button>
                            )}
                        </div>

                        {/* Allowed Domains */}
                        <div>
                            <Label>Allowed Email Domains</Label>
                            <Input value={inviteDomains} onChange={e => setInviteDomains(e.target.value)} placeholder="company.com, subsidiary.com" className="mt-1" data-testid="invite-domains" />
                            <p className="text-xs text-stone-400 mt-1">Only emails from these domains can register. Leave empty for no restriction.</p>
                        </div>

                        {/* Send Invites Section */}
                        <div className="border-t border-stone-200 pt-4 space-y-3">
                            <Label className="text-sm font-medium">Send Email Invitations</Label>
                            <Textarea
                                value={inviteEmails}
                                onChange={(e) => setInviteEmails(e.target.value)}
                                placeholder={"Enter email addresses (one per line)\njane@company.com\nbob@company.com"}
                                rows={4}
                                className="text-sm"
                                data-testid="invite-send-emails"
                            />
                            <Button
                                onClick={sendInviteEmails}
                                disabled={sendingInvites || !inviteEmails.trim()}
                                className="w-full bg-stone-900 hover:bg-stone-800"
                                data-testid="send-invitations-btn"
                            >
                                {sendingInvites ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MailCheck className="w-4 h-4 mr-1" />}
                                {sendingInvites ? 'Sending...' : 'Send Invitations'}
                            </Button>

                            {/* Send Results */}
                            {inviteSendResult && (
                                <div className="bg-stone-50 rounded-lg p-3 space-y-2" data-testid="invite-send-result">
                                    {inviteSendResult.sent_count > 0 && (
                                        <div className="flex items-center gap-2 text-sm text-emerald-700">
                                            <Check className="w-4 h-4" />
                                            <span>{inviteSendResult.sent_count} invitation(s) sent successfully</span>
                                        </div>
                                    )}
                                    {inviteSendResult.failed?.length > 0 && (
                                        <div className="space-y-1">
                                            <p className="text-sm font-medium text-red-600">Failed:</p>
                                            {inviteSendResult.failed.map((f, i) => (
                                                <div key={i} className="flex items-start gap-2 text-xs text-red-500">
                                                    <X className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                                    <span>{f.email}: {f.reason}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Rep Profile Modal */}
            <RepProfilePanel
                open={showRepProfile}
                onOpenChange={setShowRepProfile}
                userId={repProfileUser?.user_id}
                userName={repProfileUser?.name}
            />

            {/* User Profile MD Modal */}
            <UserProfileMdPanel
                open={showProfileMd}
                onOpenChange={setShowProfileMd}
                userId={profileMdUser?.user_id}
                userName={profileMdUser?.name}
            />
        </>
    );
}

// ─── User Groups Tab ────────────────────────────────────────────────────────
function UserGroupsTab() {
    const { getAuthHeader } = useAuth();
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', member_ids: [] });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [groupsRes, usersRes] = await Promise.all([
                axios.get(`${API}/admin/user-groups`, { headers: getAuthHeader() }),
                axios.get(`${API}/users`, { headers: getAuthHeader() }),
            ]);
            setGroups(groupsRes.data);
            setUsers(usersRes.data);
        } catch { toast.error('Failed to load data'); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        if (!form.name.trim()) return toast.error('Please enter a group name');
        try {
            await axios.post(`${API}/admin/user-groups`, form, { headers: getAuthHeader() });
            toast.success('Group created');
            setShowCreateModal(false);
            setForm({ name: '', description: '', member_ids: [] });
            loadData();
        } catch { toast.error('Failed to create group'); }
    };

    const handleUpdate = async () => {
        try {
            await axios.put(`${API}/admin/user-groups/${editingGroup.group_id}`, form, { headers: getAuthHeader() });
            toast.success('Group updated');
            setShowEditModal(false);
            loadData();
        } catch { toast.error('Failed to update group'); }
    };

    const handleDelete = async (groupId) => {
        if (!window.confirm('Delete this group?')) return;
        try {
            await axios.delete(`${API}/admin/user-groups/${groupId}`, { headers: getAuthHeader() });
            toast.success('Group deleted');
            loadData();
        } catch { toast.error('Failed to delete group'); }
    };

    const openEditModal = (group) => {
        setEditingGroup(group);
        setForm({
            name: group.name,
            description: group.description || '',
            member_ids: group.member_ids || [],
        });
        setShowEditModal(true);
    };

    const toggleMember = (userId) => {
        setForm(prev => ({
            ...prev,
            member_ids: prev.member_ids.includes(userId)
                ? prev.member_ids.filter(id => id !== userId)
                : [...prev.member_ids, userId]
        }));
    };

    if (loading) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900">User Groups</h2>
                    <p className="text-sm text-stone-500">Organize users into groups to control template access</p>
                </div>
                <Button onClick={() => { setForm({ name: '', description: '', member_ids: [] }); setShowCreateModal(true); }} className="bg-stone-900 hover:bg-stone-800">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Group
                </Button>
            </div>

            {groups.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-xl">
                    <Users className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No user groups yet</h3>
                    <p className="text-stone-500 mb-4">Create groups to organize users and control template access</p>
                    <Button onClick={() => setShowCreateModal(true)}>Create Your First Group</Button>
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-4">
                    {groups.map(group => (
                        <div key={group.group_id} className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="font-semibold text-stone-900">{group.name}</h3>
                                    {group.description && <p className="text-sm text-stone-500 mt-0.5">{group.description}</p>}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => openEditModal(group)} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600">
                                        <Edit3 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(group.group_id)} className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-stone-500">
                                <Users className="w-4 h-4" />
                                <span>{group.members?.length || 0} members</span>
                            </div>
                            {group.members && group.members.length > 0 && (
                                <div className="flex -space-x-2 mt-3">
                                    {group.members.slice(0, 5).map(m => (
                                        <div key={m.user_id} className="w-8 h-8 rounded-full bg-stone-100 border-2 border-white flex items-center justify-center text-xs font-semibold text-stone-900" title={m.name}>
                                            {m.name?.charAt(0) || '?'}
                                        </div>
                                    ))}
                                    {group.members.length > 5 && (
                                        <div className="w-8 h-8 rounded-full bg-stone-100 border-2 border-white flex items-center justify-center text-xs font-semibold text-stone-500">
                                            +{group.members.length - 5}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => { if (!open) { setShowCreateModal(false); setShowEditModal(false); } }}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{showEditModal ? 'Edit User Group' : 'Create User Group'}</DialogTitle>
                        <DialogDescription>
                            {showEditModal ? 'Update the group details and members.' : 'Create a new user group for template access control.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label>Group Name *</Label>
                            <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Enterprise Sales Team" className="mt-1" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description of this group" className="mt-1" rows={2} />
                        </div>
                        <div>
                            <Label>Members ({form.member_ids.length} selected)</Label>
                            <div className="mt-2 border border-stone-200 rounded-lg max-h-48 overflow-y-auto">
                                {users.map(user => (
                                    <label key={user.user_id} className="flex items-center gap-3 p-3 hover:bg-stone-50 cursor-pointer border-b border-stone-100 last:border-b-0">
                                        <input type="checkbox" checked={form.member_ids.includes(user.user_id)} onChange={() => toggleMember(user.user_id)} className="rounded border-stone-300 text-stone-700" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-stone-800">{user.name}</p>
                                            <p className="text-xs text-stone-500">{user.email}</p>
                                        </div>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 capitalize">{user.role?.replace('_', ' ')}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}>Cancel</Button>
                        <Button onClick={showEditModal ? handleUpdate : handleCreate} className="bg-stone-900 hover:bg-stone-800">
                            {showEditModal ? 'Save Changes' : 'Create Group'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ─── Templates Tab ──────────────────────────────────────────────────────────

// Template Preview Modal
function TemplatePreview({ template, open, onClose }) {
    if (!template) return null;
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Template Preview</DialogTitle>
                    <DialogDescription>This is how a Deal Room created from this template will appear</DialogDescription>
                </DialogHeader>
                <div className="border border-stone-200 rounded-xl overflow-hidden mt-2">
                    <div className="bg-gradient-to-r from-stone-900 to-stone-700 p-6 text-white">
                        <h2 className="text-xl font-bold">{template.name}</h2>
                        {template.description && <p className="opacity-80 text-sm mt-1">{template.description}</p>}
                    </div>
                    <div className="p-6 bg-stone-50">
                        {(!template.sections || template.sections.length === 0) ? (
                            <p className="text-center text-stone-400 py-8">No sections in this template</p>
                        ) : (
                            <div className="space-y-6">
                                {template.sections.map((section, idx) => (
                                    <div key={idx} className="bg-white rounded-xl p-5 border border-stone-200">
                                        <h3 className="font-semibold text-stone-900 mb-1">{section.name}</h3>
                                        {section.description && <p className="text-sm text-stone-500 mb-4">{section.description}</p>}
                                        {section.items && section.items.length > 0 ? (
                                            <div className="space-y-2">
                                                {section.items.map((item, iIdx) => (
                                                    <div key={iIdx} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                                                        {item.content_type === 'richtext' ? <FileText className="w-5 h-5 text-emerald-600" /> :
                                                         item.content_type === 'link' ? <Globe className="w-5 h-5 text-blue-600" /> :
                                                         <FileText className="w-5 h-5 text-stone-700" />}
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-stone-800">{item.title}</p>
                                                            <p className="text-xs text-stone-400 capitalize">{item.content_type}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-stone-400 italic">Empty section — content to be added when creating a Deal Room</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Template Builder — full page detail view for creating/editing a template
function TemplateBuilder({ template, groups, users, onSave, onCancel }) {
    const [form, setForm] = useState({
        name: template?.name || '',
        description: template?.description || '',
        access_type: template?.access_type || 'all',
        allowed_group_ids: template?.allowed_group_ids || [],
        allowed_user_ids: template?.allowed_user_ids || [],
        sections: template?.sections || [],
    });
    const [expandedSection, setExpandedSection] = useState(null);
    const [showAddSection, setShowAddSection] = useState(false);
    const [newSection, setNewSection] = useState({ name: '', description: '' });
    const [showAddItem, setShowAddItem] = useState(false);
    const [addItemToIdx, setAddItemToIdx] = useState(null);
    const [newItem, setNewItem] = useState({ title: '', content_type: 'richtext', rich_content: '' });
    const [showPreview, setShowPreview] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!form.name.trim()) return toast.error('Please enter a template name');
        setSaving(true);
        try {
            await onSave(form);
        } finally {
            setSaving(false);
        }
    };

    const addSection = () => {
        if (!newSection.name.trim()) return toast.error('Enter a section name');
        setForm(prev => ({ ...prev, sections: [...prev.sections, { ...newSection, items: [] }] }));
        setNewSection({ name: '', description: '' });
        setShowAddSection(false);
        setExpandedSection(form.sections.length); // auto-expand the new section
    };

    const removeSection = (idx) => {
        setForm(prev => ({ ...prev, sections: prev.sections.filter((_, i) => i !== idx) }));
        if (expandedSection === idx) setExpandedSection(null);
    };

    const updateSection = (idx, field, value) => {
        setForm(prev => ({
            ...prev,
            sections: prev.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s),
        }));
    };

    const openAddItem = (sectionIdx) => {
        setAddItemToIdx(sectionIdx);
        setNewItem({ title: '', content_type: 'richtext', rich_content: '' });
        setShowAddItem(true);
    };

    const addItem = () => {
        if (!newItem.title.trim()) return toast.error('Enter an item title');
        setForm(prev => ({
            ...prev,
            sections: prev.sections.map((s, i) =>
                i === addItemToIdx ? { ...s, items: [...(s.items || []), { ...newItem }] } : s
            ),
        }));
        setNewItem({ title: '', content_type: 'richtext', rich_content: '' });
        setShowAddItem(false);
    };

    const removeItem = (sectionIdx, itemIdx) => {
        setForm(prev => ({
            ...prev,
            sections: prev.sections.map((s, i) =>
                i === sectionIdx ? { ...s, items: s.items.filter((_, j) => j !== itemIdx) } : s
            ),
        }));
    };

    const moveSection = (idx, direction) => {
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= form.sections.length) return;
        setForm(prev => {
            const sections = [...prev.sections];
            [sections[idx], sections[newIdx]] = [sections[newIdx], sections[idx]];
            return { ...prev, sections };
        });
        setExpandedSection(newIdx);
    };

    const totalItems = form.sections.reduce((sum, s) => sum + (s.items?.length || 0), 0);

    return (
        <div data-testid="template-builder">
            {/* Builder Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button onClick={onCancel} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600" data-testid="template-builder-back">
                        <ChevronRight className="w-5 h-5 rotate-180" />
                    </button>
                    <div>
                        <h2 className="text-lg font-semibold text-stone-900">
                            {template ? 'Edit Template' : 'Create Template'}
                        </h2>
                        <p className="text-sm text-stone-500">
                            {form.sections.length} sections, {totalItems} content items
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setShowPreview(true)} data-testid="template-preview-btn">
                        <Eye className="w-4 h-4 mr-2" />Preview
                    </Button>
                    <Button variant="outline" onClick={onCancel}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-stone-900 hover:bg-stone-800" data-testid="template-save-btn">
                        {saving ? 'Saving...' : template ? 'Save Changes' : 'Create Template'}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {/* Left: Settings Panel */}
                <div className="col-span-4 space-y-4">
                    <div className="bg-white rounded-xl border border-stone-200 p-5">
                        <h3 className="font-semibold text-stone-900 mb-4">Template Details</h3>
                        <div className="space-y-3">
                            <div>
                                <Label>Name *</Label>
                                <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Enterprise Sales Template" className="mt-1" data-testid="template-name-input" />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Describe when to use this template..." className="mt-1" rows={3} data-testid="template-desc-input" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-stone-200 p-5">
                        <h3 className="font-semibold text-stone-900 mb-4">Access Control</h3>
                        <Select value={form.access_type} onValueChange={(v) => setForm(prev => ({ ...prev, access_type: v }))}>
                            <SelectTrigger data-testid="template-access-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Users</SelectItem>
                                <SelectItem value="groups">Specific Groups</SelectItem>
                                <SelectItem value="users">Specific Users</SelectItem>
                            </SelectContent>
                        </Select>

                        {form.access_type === 'groups' && (
                            <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                                {groups.map(group => (
                                    <label key={group.group_id} className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded cursor-pointer">
                                        <input type="checkbox" checked={form.allowed_group_ids.includes(group.group_id)}
                                            onChange={(e) => setForm(prev => ({
                                                ...prev, allowed_group_ids: e.target.checked
                                                    ? [...prev.allowed_group_ids, group.group_id]
                                                    : prev.allowed_group_ids.filter(id => id !== group.group_id)
                                            }))}
                                            className="rounded border-stone-300" />
                                        <span className="text-sm">{group.name}</span>
                                        <span className="text-xs text-stone-400">({group.members?.length || 0})</span>
                                    </label>
                                ))}
                                {groups.length === 0 && <p className="text-xs text-stone-400 py-2">No groups created yet</p>}
                            </div>
                        )}

                        {form.access_type === 'users' && (
                            <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                                {users.map(user => (
                                    <label key={user.user_id} className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded cursor-pointer">
                                        <input type="checkbox" checked={form.allowed_user_ids.includes(user.user_id)}
                                            onChange={(e) => setForm(prev => ({
                                                ...prev, allowed_user_ids: e.target.checked
                                                    ? [...prev.allowed_user_ids, user.user_id]
                                                    : prev.allowed_user_ids.filter(id => id !== user.user_id)
                                            }))}
                                            className="rounded border-stone-300" />
                                        <span className="text-sm">{user.name}</span>
                                        <span className="text-xs text-stone-400">{user.email}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Sections Builder */}
                <div className="col-span-8">
                    <div className="bg-white rounded-xl border border-stone-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-stone-900">Sections & Content</h3>
                            <Button size="sm" onClick={() => setShowAddSection(true)} data-testid="add-section-btn">
                                <Plus className="w-4 h-4 mr-1" /> Add Section
                            </Button>
                        </div>

                        {form.sections.length === 0 ? (
                            <div className="border-2 border-dashed border-stone-200 rounded-xl p-10 text-center">
                                <Layers className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                                <p className="text-stone-500 mb-1">No sections yet</p>
                                <p className="text-sm text-stone-400 mb-4">Add sections to define the template structure that salespeople will use</p>
                                <Button variant="outline" onClick={() => setShowAddSection(true)}>Add First Section</Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {form.sections.map((section, idx) => {
                                    const isExpanded = expandedSection === idx;
                                    return (
                                        <div key={idx} className={`border rounded-xl transition-colors ${isExpanded ? 'border-stone-300 bg-stone-100/30' : 'border-stone-200'}`} data-testid={`template-section-${idx}`}>
                                            {/* Section Header */}
                                            <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setExpandedSection(isExpanded ? null : idx)}>
                                                <ChevronRight className={`w-4 h-4 text-stone-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-stone-800">{section.name}</p>
                                                    <p className="text-xs text-stone-400">
                                                        {section.items?.length || 0} items
                                                        {section.description ? ` — ${section.description}` : ''}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                                    <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="p-1.5 hover:bg-white rounded text-stone-400 hover:text-stone-600 disabled:opacity-30" title="Move up">
                                                        <ChevronRight className="w-3.5 h-3.5 -rotate-90" />
                                                    </button>
                                                    <button onClick={() => moveSection(idx, 1)} disabled={idx === form.sections.length - 1} className="p-1.5 hover:bg-white rounded text-stone-400 hover:text-stone-600 disabled:opacity-30" title="Move down">
                                                        <ChevronRight className="w-3.5 h-3.5 rotate-90" />
                                                    </button>
                                                    <button onClick={() => openAddItem(idx)} className="p-1.5 hover:bg-white rounded text-stone-400 hover:text-stone-700" title="Add item">
                                                        <Plus className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => removeSection(idx)} className="p-1.5 hover:bg-white rounded text-stone-400 hover:text-red-500" title="Delete section">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Expanded: show items */}
                                            {isExpanded && (
                                                <div className="px-4 pb-4">
                                                    {/* Inline edit section name/desc */}
                                                    <div className="grid grid-cols-2 gap-3 mb-4 pb-3 border-b border-stone-200">
                                                        <div>
                                                            <Label className="text-xs">Section Name</Label>
                                                            <Input value={section.name} onChange={(e) => updateSection(idx, 'name', e.target.value)} className="mt-1 h-8 text-sm" />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs">Description</Label>
                                                            <Input value={section.description || ''} onChange={(e) => updateSection(idx, 'description', e.target.value)} className="mt-1 h-8 text-sm" placeholder="Optional" />
                                                        </div>
                                                    </div>

                                                    {(!section.items || section.items.length === 0) ? (
                                                        <div className="text-center py-4">
                                                            <p className="text-sm text-stone-400 mb-2">No content items</p>
                                                            <Button size="sm" variant="outline" onClick={() => openAddItem(idx)}>
                                                                <Plus className="w-3 h-3 mr-1" /> Add Item
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            {section.items.map((item, iIdx) => (
                                                                <div key={iIdx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-stone-100 group" data-testid={`template-item-${idx}-${iIdx}`}>
                                                                    <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                                                                        item.content_type === 'richtext' ? 'bg-emerald-50' :
                                                                        item.content_type === 'link' ? 'bg-blue-50' : 'bg-stone-100'
                                                                    }`}>
                                                                        {item.content_type === 'richtext' ? <FileText className="w-4 h-4 text-emerald-600" /> :
                                                                         item.content_type === 'link' ? <Globe className="w-4 h-4 text-blue-600" /> :
                                                                         <FileText className="w-4 h-4 text-stone-700" />}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-medium text-stone-800">{item.title}</p>
                                                                        <p className="text-xs text-stone-400 capitalize">{item.content_type === 'richtext' ? 'Rich Text' : item.content_type}</p>
                                                                    </div>
                                                                    {item.rich_content && (
                                                                        <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full">Has content</span>
                                                                    )}
                                                                    <button onClick={() => removeItem(idx, iIdx)} className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded text-stone-400 hover:text-red-500 transition-opacity">
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <Button size="sm" variant="ghost" onClick={() => openAddItem(idx)} className="w-full text-stone-400 hover:text-stone-700">
                                                                <Plus className="w-3 h-3 mr-1" /> Add Item
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Section Dialog */}
            <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Section</DialogTitle>
                        <DialogDescription>Create a new section for this template</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-3">
                        <div>
                            <Label>Section Name *</Label>
                            <Input value={newSection.name} onChange={(e) => setNewSection(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Getting Started" className="mt-1" data-testid="new-section-name" />
                        </div>
                        <div>
                            <Label>Description (optional)</Label>
                            <Input value={newSection.description} onChange={(e) => setNewSection(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description" className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddSection(false)}>Cancel</Button>
                        <Button onClick={addSection} className="bg-stone-900 hover:bg-stone-800" data-testid="confirm-add-section">Add Section</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Content Item Dialog */}
            <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
                <DialogContent className={newItem.content_type === 'richtext' ? 'sm:max-w-3xl' : 'sm:max-w-lg'}>
                    <DialogHeader>
                        <DialogTitle>Add Content Item</DialogTitle>
                        <DialogDescription>
                            Adding to: {addItemToIdx !== null ? form.sections[addItemToIdx]?.name : ''}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-3">
                        <div>
                            <Label>Title *</Label>
                            <Input value={newItem.title} onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g., Welcome Message" className="mt-1" data-testid="new-item-title" />
                        </div>
                        <div>
                            <Label>Content Type</Label>
                            <Select value={newItem.content_type} onValueChange={(v) => setNewItem(prev => ({ ...prev, content_type: v }))}>
                                <SelectTrigger className="mt-1" data-testid="new-item-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="richtext">Rich Text</SelectItem>
                                    <SelectItem value="document">Document Placeholder</SelectItem>
                                    <SelectItem value="link">External Link</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {newItem.content_type === 'richtext' && (
                            <div>
                                <Label>Content (optional — can be filled later)</Label>
                                <div className="mt-1">
                                    <RichTextEditor content={newItem.rich_content} onChange={(html) => setNewItem(prev => ({ ...prev, rich_content: html }))} minHeight="200px" showContentLibrary={true} />
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
                        <Button onClick={addItem} className="bg-stone-900 hover:bg-stone-800" data-testid="confirm-add-item">Add Item</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview */}
            <TemplatePreview template={form} open={showPreview} onClose={() => setShowPreview(false)} />
        </div>
    );
}

// Template List + Builder wrapper
function TemplatesTab() {
    const { getAuthHeader } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState('list'); // 'list' | 'create' | 'edit'
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [templatesRes, groupsRes, usersRes] = await Promise.all([
                axios.get(`${API}/admin/templates`, { headers: getAuthHeader() }),
                axios.get(`${API}/admin/user-groups`, { headers: getAuthHeader() }),
                axios.get(`${API}/users`, { headers: getAuthHeader() }),
            ]);
            setTemplates(templatesRes.data);
            setGroups(groupsRes.data);
            setUsers(usersRes.data);
        } catch { toast.error('Failed to load templates'); }
        finally { setLoading(false); }
    };

    const handleCreate = async (formData) => {
        await axios.post(`${API}/admin/templates`, formData, { headers: getAuthHeader() });
        toast.success('Template created');
        setMode('list');
        loadData();
    };

    const handleUpdate = async (formData) => {
        await axios.put(`${API}/admin/templates/${editingTemplate.template_id}`, formData, { headers: getAuthHeader() });
        toast.success('Template updated');
        setMode('list');
        setEditingTemplate(null);
        loadData();
    };

    const handleDelete = async (templateId) => {
        if (!window.confirm('Delete this template?')) return;
        try {
            await axios.delete(`${API}/admin/templates/${templateId}`, { headers: getAuthHeader() });
            toast.success('Template deleted');
            loadData();
        } catch { toast.error('Failed to delete template'); }
    };

    if (loading) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    // Builder view
    if (mode === 'create') {
        return <TemplateBuilder groups={groups} users={users} onSave={handleCreate} onCancel={() => setMode('list')} />;
    }
    if (mode === 'edit' && editingTemplate) {
        return <TemplateBuilder template={editingTemplate} groups={groups} users={users} onSave={handleUpdate} onCancel={() => { setMode('list'); setEditingTemplate(null); }} />;
    }

    // List view
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900">Deal Room Templates</h2>
                    <p className="text-sm text-stone-500">Create templates that sales teams can use to quickly set up deal rooms</p>
                </div>
                <Button onClick={() => setMode('create')} className="bg-stone-900 hover:bg-stone-800" data-testid="create-template-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                </Button>
            </div>

            {templates.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-xl">
                    <Layers className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No templates yet</h3>
                    <p className="text-stone-500 mb-4">Create templates with pre-configured sections and content</p>
                    <Button onClick={() => setMode('create')} data-testid="create-first-template-btn">Create Your First Template</Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {templates.map(template => (
                        <div key={template.template_id} className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`template-card-${template.template_id}`}>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                                    <Layers className="w-6 h-6 text-stone-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <h3 className="font-semibold text-stone-900">{template.name}</h3>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${template.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
                                            {template.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    {template.description && <p className="text-sm text-stone-500 truncate">{template.description}</p>}
                                    <div className="flex items-center gap-4 mt-1.5 text-xs text-stone-400">
                                        <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" />{template.sections?.length || 0} sections</span>
                                        <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{template.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} items</span>
                                        <span className="flex items-center gap-1">
                                            {template.access_type === 'all' ? <Globe className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                                            {template.access_type === 'all' ? 'All Users' :
                                             template.access_type === 'groups' ? `${template.allowed_group_ids?.length || 0} groups` :
                                             `${template.allowed_user_ids?.length || 0} users`}
                                        </span>
                                        {template.created_by_name && <span>by {template.created_by_name}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Button variant="outline" size="sm" onClick={() => setPreviewTemplate(template)} data-testid={`preview-template-${template.template_id}`}>
                                        <Eye className="w-4 h-4 mr-1.5" />Preview
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => { setEditingTemplate(template); setMode('edit'); }} data-testid={`edit-template-${template.template_id}`}>
                                        <Edit3 className="w-4 h-4 mr-1.5" />Edit
                                    </Button>
                                    <button onClick={() => handleDelete(template.template_id)} className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500" data-testid={`delete-template-${template.template_id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Preview Modal */}
            <TemplatePreview template={previewTemplate} open={!!previewTemplate} onClose={() => setPreviewTemplate(null)} />
        </div>
    );
}

// ─── Media Library Tab ──────────────────────────────────────────────────────
function MediaLibraryTab() {
    const { getAuthHeader } = useAuth();
    const [items, setItems] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedFolder, setSelectedFolder] = useState('all');
    const [selectedType, setSelectedType] = useState('all');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
    const [uploadForm, setUploadForm] = useState({ name: '', description: '', file_type: 'document', file: null, folder: 'general', tags: '', external_url: '' });
    const [uploading, setUploading] = useState(false);

    useEffect(() => { loadData(); }, [search, selectedFolder, selectedType]);

    const loadData = async () => {
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (selectedFolder !== 'all') params.append('folder', selectedFolder);
            if (selectedType !== 'all') params.append('file_type', selectedType);

            const [itemsRes, foldersRes] = await Promise.all([
                axios.get(`${API}/media-library?${params}`, { headers: getAuthHeader() }),
                axios.get(`${API}/media-library/folders`, { headers: getAuthHeader() }),
            ]);
            setItems(itemsRes.data);
            setFolders(foldersRes.data);
        } catch { toast.error('Failed to load media'); }
        finally { setLoading(false); }
    };

    const handleUpload = async () => {
        if (uploadMode === 'url') {
            if (!uploadForm.external_url.trim()) return toast.error('Please enter a URL');
            setUploading(true);
            try {
                await axios.post(`${API}/media-library`, {
                    name: uploadForm.name || uploadForm.external_url,
                    description: uploadForm.description,
                    file_type: uploadForm.file_type,
                    external_url: uploadForm.external_url,
                    folder: uploadForm.folder,
                    tags: uploadForm.tags.split(',').map(t => t.trim()).filter(t => t),
                }, { headers: getAuthHeader() });
                toast.success('Media added');
                setShowUploadModal(false);
                setUploadForm({ name: '', description: '', file_type: 'document', file: null, folder: 'general', tags: '', external_url: '' });
                setUploadMode('file');
                loadData();
            } catch { toast.error('Failed to add media'); }
            finally { setUploading(false); }
            return;
        }
        if (!uploadForm.file) return toast.error('Please select a file');
        setUploading(true);
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const base64 = e.target.result.split(',')[1];
                await axios.post(`${API}/media-library`, {
                    name: uploadForm.name || uploadForm.file.name,
                    description: uploadForm.description,
                    file_type: uploadForm.file_type,
                    file_name: uploadForm.file.name,
                    file_size: uploadForm.file.size,
                    file_data: base64,
                    folder: uploadForm.folder,
                    tags: uploadForm.tags.split(',').map(t => t.trim()).filter(t => t),
                }, { headers: getAuthHeader() });
                toast.success('Media uploaded');
                setShowUploadModal(false);
                setUploadForm({ name: '', description: '', file_type: 'document', file: null, folder: 'general', tags: '', external_url: '' });
                setUploadMode('file');
                loadData();
            };
            reader.readAsDataURL(uploadForm.file);
        } catch { toast.error('Upload failed'); }
        finally { setUploading(false); }
    };

    const handleDelete = async (mediaId) => {
        if (!window.confirm('Delete this media?')) return;
        try {
            await axios.delete(`${API}/media-library/${mediaId}`, { headers: getAuthHeader() });
            toast.success('Media deleted');
            loadData();
        } catch { toast.error('Delete failed'); }
    };

    const getFileIcon = (type) => {
        switch (type) {
            case 'image': return Image;
            case 'video': return Video;
            default: return FileText;
        }
    };

    if (loading) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900">Media Library</h2>
                    <p className="text-sm text-stone-500">Shared media assets accessible by all sales team members</p>
                </div>
                <Button onClick={() => setShowUploadModal(true)} className="bg-stone-900 hover:bg-stone-800">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Media
                </Button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input placeholder="Search media..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
                </div>
                <Select value={selectedFolder} onValueChange={setSelectedFolder}>
                    <SelectTrigger className="w-40">
                        <FolderOpen className="w-4 h-4 mr-2 text-stone-400" />
                        <SelectValue placeholder="All Folders" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Folders</SelectItem>
                        {folders.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="document">Documents</SelectItem>
                        <SelectItem value="image">Images</SelectItem>
                        <SelectItem value="video">Videos</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {items.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-xl">
                    <Image className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No media yet</h3>
                    <p className="text-stone-500 mb-4">Upload images, videos, and documents to share with your team</p>
                    <Button onClick={() => setShowUploadModal(true)}>Upload Your First Media</Button>
                </div>
            ) : (
                <div className="grid grid-cols-4 gap-4">
                    {items.map(item => {
                        const IconComponent = getFileIcon(item.file_type);
                        const isExternal = !!item.external_url;
                        return (
                            <div key={item.media_id} className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow group" data-testid={`media-item-${item.media_id}`}>
                                <div className={`h-32 flex items-center justify-center relative ${
                                    item.file_type === 'image' ? 'bg-amber-50' :
                                    item.file_type === 'video' ? 'bg-violet-50' : 'bg-stone-100'
                                }`}>
                                    <IconComponent className={`w-12 h-12 ${
                                        item.file_type === 'image' ? 'text-amber-400' :
                                        item.file_type === 'video' ? 'text-violet-400' : 'text-stone-500'
                                    }`} />
                                    {isExternal && (
                                        <span className="absolute top-2 right-2 p-1 bg-white/80 rounded-md" title="External link">
                                            <Globe className="w-3.5 h-3.5 text-blue-500" />
                                        </span>
                                    )}
                                </div>
                                <div className="p-3">
                                    <p className="text-sm font-medium text-stone-800 truncate">{item.name}</p>
                                    <div className="flex items-center justify-between mt-1">
                                        <span className="text-xs text-stone-400">
                                            {isExternal ? 'External link' : formatFileSize(item.file_size)}
                                        </span>
                                        <span className="text-xs px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full">{item.folder}</span>
                                    </div>
                                    <div className="flex items-center justify-end gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {isExternal && (
                                            <a href={item.external_url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-blue-50 rounded text-stone-400 hover:text-blue-500">
                                                <Eye className="w-4 h-4" />
                                            </a>
                                        )}
                                        <button onClick={() => handleDelete(item.media_id)} className="p-1.5 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Upload Modal */}
            <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Media</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Upload Mode Toggle */}
                        <div className="flex gap-2 p-1 bg-stone-100 rounded-lg">
                            <button
                                onClick={() => setUploadMode('file')}
                                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${uploadMode === 'file' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}
                                data-testid="upload-mode-file"
                            >
                                <Upload className="w-4 h-4 inline mr-1.5" />Upload File
                            </button>
                            <button
                                onClick={() => setUploadMode('url')}
                                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${uploadMode === 'url' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}
                                data-testid="upload-mode-url"
                            >
                                <Globe className="w-4 h-4 inline mr-1.5" />External URL
                            </button>
                        </div>

                        {uploadMode === 'file' ? (
                            <div>
                                <Label>File</Label>
                                <Input type="file" onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setUploadForm(prev => ({
                                            ...prev,
                                            file,
                                            name: prev.name || file.name,
                                            file_type: file.type.startsWith('image/') ? 'image' :
                                                       file.type.startsWith('video/') ? 'video' : 'document'
                                        }));
                                    }
                                }} className="mt-1" data-testid="media-file-input" />
                            </div>
                        ) : (
                            <div>
                                <Label>External URL *</Label>
                                <Input
                                    value={uploadForm.external_url}
                                    onChange={(e) => setUploadForm(prev => ({ ...prev, external_url: e.target.value }))}
                                    placeholder="https://example.com/document.pdf"
                                    className="mt-1"
                                    data-testid="media-url-input"
                                />
                            </div>
                        )}

                        <div>
                            <Label>Name</Label>
                            <Input value={uploadForm.name} onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))} className="mt-1" placeholder={uploadMode === 'url' ? 'Display name for this media' : ''} data-testid="media-name-input" />
                        </div>
                        <div>
                            <Label>Description (optional)</Label>
                            <Textarea value={uploadForm.description} onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))} className="mt-1" rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>File Type</Label>
                                <Select value={uploadForm.file_type} onValueChange={(v) => setUploadForm(prev => ({ ...prev, file_type: v }))}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="document">Document</SelectItem>
                                        <SelectItem value="image">Image</SelectItem>
                                        <SelectItem value="video">Video</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Folder</Label>
                                <Input value={uploadForm.folder} onChange={(e) => setUploadForm(prev => ({ ...prev, folder: e.target.value }))} placeholder="general" className="mt-1" />
                            </div>
                        </div>
                        <div>
                            <Label>Tags (comma-separated)</Label>
                            <Input value={uploadForm.tags} onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))} placeholder="sales, proposal, product" className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowUploadModal(false)}>Cancel</Button>
                        <Button onClick={handleUpload} disabled={uploading || (uploadMode === 'file' && !uploadForm.file) || (uploadMode === 'url' && !uploadForm.external_url.trim())} className="bg-stone-900 hover:bg-stone-800" data-testid="confirm-upload-btn">
                            {uploading ? 'Saving...' : uploadMode === 'url' ? 'Add Media' : 'Upload'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ─── Main Admin Page ────────────────────────────────────────────────────────
// ─── AI Skills Tab ──────────────────────────────────────────────────────────

const SKILL_CATEGORIES = [
    { value: 'general', label: 'General' },
    { value: 'pipeline', label: 'Pipeline' },
    { value: 'accounts', label: 'Companies' },
    { value: 'deals', label: 'Deals' },
    { value: 'activities', label: 'Activities' },
    { value: 'coaching', label: 'Coaching' },
];

const SKILL_ICONS = [
    'sparkles', 'alert-triangle', 'bar-chart-3', 'building-2', 'target',
    'check-square', 'presentation', 'zap', 'search', 'shield',
];

const iconMap = {
    'sparkles': Sparkles, 'alert-triangle': AlertTriangle, 'bar-chart-3': BarChart3,
    'building-2': Building2, 'target': Target, 'check-square': CheckSquare,
    'presentation': Presentation, 'zap': Zap, 'search': Search, 'shield': Shield,
};

const categoryColors = {
    pipeline: 'bg-blue-50 text-blue-700 border-blue-200',
    accounts: 'bg-amber-50 text-amber-700 border-amber-200',
    deals: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    activities: 'bg-violet-50 text-violet-700 border-violet-200',
    coaching: 'bg-rose-50 text-rose-700 border-rose-200',
    general: 'bg-stone-50 text-stone-700 border-stone-200',
};

function AISkillsTab() {
    const { getAuthHeader } = useAuth();
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSkill, setEditingSkill] = useState(null);
    const [form, setForm] = useState({
        name: '', description: '', prompt_template: '',
        category: 'general', icon: 'sparkles', is_active: true,
    });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const res = await axios.get(`${API}/admin/skills`, { headers: getAuthHeader() });
            setSkills(res.data);
        } catch { toast.error('Failed to load skills'); }
        finally { setLoading(false); }
    };

    const resetForm = () => setForm({ name: '', description: '', prompt_template: '', category: 'general', icon: 'sparkles', is_active: true });

    const openCreate = () => { resetForm(); setEditingSkill(null); setShowModal(true); };

    const openEdit = (skill) => {
        setEditingSkill(skill);
        setForm({
            name: skill.name, description: skill.description,
            prompt_template: skill.prompt_template,
            category: skill.category || 'general',
            icon: skill.icon || 'sparkles',
            is_active: skill.is_active !== false,
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.prompt_template.trim()) return toast.error('Name and prompt template are required');
        try {
            if (editingSkill) {
                await axios.put(`${API}/admin/skills/${editingSkill.skill_id}`, form, { headers: getAuthHeader() });
                toast.success('Skill updated');
            } else {
                await axios.post(`${API}/admin/skills`, form, { headers: getAuthHeader() });
                toast.success('Skill created');
            }
            setShowModal(false);
            loadData();
        } catch { toast.error('Failed to save skill'); }
    };

    const handleDelete = async (skillId) => {
        if (!window.confirm('Delete this skill?')) return;
        try {
            await axios.delete(`${API}/admin/skills/${skillId}`, { headers: getAuthHeader() });
            toast.success('Skill deleted');
            loadData();
        } catch { toast.error('Failed to delete skill'); }
    };

    const toggleActive = async (skill) => {
        try {
            await axios.put(`${API}/admin/skills/${skill.skill_id}`, { is_active: !skill.is_active }, { headers: getAuthHeader() });
            loadData();
        } catch { toast.error('Failed to toggle skill'); }
    };

    if (loading) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900">AI Skills</h2>
                    <p className="text-sm text-stone-500">Create prompt templates that salespeople can use with the AI Assistant</p>
                </div>
                <Button onClick={openCreate} className="bg-stone-900 hover:bg-stone-800" data-testid="create-skill-btn">
                    <Plus className="w-4 h-4 mr-2" />Create Skill
                </Button>
            </div>

            {skills.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-xl">
                    <Sparkles className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No skills yet</h3>
                    <p className="text-stone-500 mb-4">Create skills that will appear as suggestions in the AI Assistant</p>
                    <Button onClick={openCreate}>Create Your First Skill</Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {skills.map(skill => {
                        const IconComp = iconMap[skill.icon] || Sparkles;
                        const catColor = categoryColors[skill.category] || categoryColors.general;
                        return (
                            <div key={skill.skill_id} className={`bg-white border rounded-xl p-5 transition-all ${skill.is_active ? 'border-stone-200 hover:shadow-md' : 'border-stone-100 opacity-60'}`} data-testid={`skill-card-${skill.skill_id}`}>
                                <div className="flex items-start gap-4">
                                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${catColor.split(' ')[0]}`}>
                                        <IconComp className={`w-5 h-5 ${catColor.split(' ')[1]}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <h3 className="font-semibold text-stone-900">{skill.name}</h3>
                                            <span className={`text-xs px-2 py-0.5 rounded-full border ${catColor}`}>{skill.category}</span>
                                            {!skill.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-400">Inactive</span>}
                                        </div>
                                        <p className="text-sm text-stone-500">{skill.description}</p>
                                        <p className="text-xs text-stone-400 mt-1.5 font-mono bg-stone-50 rounded-md px-2 py-1 truncate">
                                            {skill.prompt_template}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <button onClick={() => toggleActive(skill)} className="p-2 hover:bg-stone-100 rounded-lg transition-colors" title={skill.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-skill-${skill.skill_id}`}>
                                            {skill.is_active ?
                                                <ToggleRight className="w-5 h-5 text-emerald-500" /> :
                                                <ToggleLeft className="w-5 h-5 text-stone-400" />}
                                        </button>
                                        <button onClick={() => openEdit(skill)} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600" data-testid={`edit-skill-${skill.skill_id}`}>
                                            <Edit3 className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleDelete(skill.skill_id)} className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500" data-testid={`delete-skill-${skill.skill_id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create/Edit Skill Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingSkill ? 'Edit Skill' : 'Create Skill'}</DialogTitle>
                        <DialogDescription>
                            {editingSkill ? 'Update the skill configuration' : 'Create a new AI skill that salespeople can use'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-3">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label>Skill Name *</Label>
                                <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Pipeline Risk Review" className="mt-1" data-testid="skill-name-input" />
                            </div>
                            <div className="col-span-2">
                                <Label>Description</Label>
                                <Input value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Short description shown to salespeople" className="mt-1" />
                            </div>
                        </div>
                        <div>
                            <Label>Prompt Template *</Label>
                            <Textarea value={form.prompt_template} onChange={(e) => setForm(prev => ({ ...prev, prompt_template: e.target.value }))}
                                placeholder="The actual prompt sent to the AI when this skill is used. Reference CRM data naturally — the AI has access to the full pipeline, accounts, activities, etc."
                                className="mt-1 font-mono text-sm" rows={4} data-testid="skill-prompt-input" />
                            <p className="text-xs text-stone-400 mt-1">The AI has access to real-time CRM data (deals, accounts, activities, contacts) and will use it to answer this prompt.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Category</Label>
                                <Select value={form.category} onValueChange={(v) => setForm(prev => ({ ...prev, category: v }))}>
                                    <SelectTrigger className="mt-1" data-testid="skill-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SKILL_CATEGORIES.map(c => (
                                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Icon</Label>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                    {SKILL_ICONS.map(ic => {
                                        const IC = iconMap[ic] || Sparkles;
                                        return (
                                            <button key={ic} onClick={() => setForm(prev => ({ ...prev, icon: ic }))}
                                                className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-colors ${form.icon === ic ? 'border-stone-400 bg-stone-100 text-stone-700' : 'border-stone-200 text-stone-400 hover:text-stone-600'}`}>
                                                <IC className="w-4 h-4" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch checked={form.is_active} onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))} />
                            <Label className="cursor-pointer">Active — visible in AI Assistant</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button onClick={handleSave} className="bg-stone-900 hover:bg-stone-800" data-testid="save-skill-btn">
                            {editingSkill ? 'Save Changes' : 'Create Skill'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


// ─── Voice Profiles Tab ──────────────────────────────────────────────────────
function VoiceProfilesTab() {
    const { getAuthHeader } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [questions, setQuestions] = useState({});
    const [expandedCat, setExpandedCat] = useState(null);

    useEffect(() => { loadUsers(); }, []);

    const loadUsers = async () => {
        try {
            const [usersRes, qRes] = await Promise.all([
                axios.get(`${API}/admin/voice-profiles`, { headers: getAuthHeader() }),
                axios.get(`${API}/voice-profile/questions`, { headers: getAuthHeader() }),
            ]);
            setUsers(usersRes.data);
            setQuestions(qRes.data);
        } catch { toast.error('Failed to load voice profiles'); }
        finally { setLoading(false); }
    };

    const viewProfile = async (userId) => {
        try {
            const res = await axios.get(`${API}/admin/voice-profiles/${userId}`, { headers: getAuthHeader() });
            setProfile(res.data);
            setSelectedUser(users.find(u => u.user_id === userId));
            setExpandedCat(null);
        } catch { toast.error('Failed to load profile'); }
    };

    if (loading) return <div className="flex items-center justify-center h-32"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    if (selectedUser && profile) {
        const answers = profile.answers || {};
        return (
            <div data-testid="voice-profile-detail">
                <button onClick={() => { setSelectedUser(null); setProfile(null); }} className="flex items-center gap-1 text-sm text-stone-700 hover:text-stone-900 mb-4" data-testid="back-to-profiles">
                    <ChevronRight className="w-4 h-4 rotate-180" />Back to all profiles
                </button>
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center text-stone-700 font-bold">{selectedUser.name?.charAt(0) || '?'}</div>
                    <div>
                        <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedUser.name}</h3>
                        <p className="text-xs text-stone-500">{selectedUser.email} &middot; {profile.completion_pct || 0}% complete &middot; {profile.answered_count || 0} answers</p>
                    </div>
                </div>
                {Object.keys(answers).length === 0 ? (
                    <div className="text-center py-10 text-stone-400">
                        <Mic className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        <p>This user has not started their voice profile yet.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {Object.entries(questions).map(([catKey, cat]) => {
                            const catAnswers = answers[catKey] || {};
                            const hasAnswers = Object.values(catAnswers).some(v => v?.trim());
                            if (!hasAnswers) return null;
                            const isExpanded = expandedCat === catKey;
                            return (
                                <div key={catKey} className="border border-stone-200 rounded-lg overflow-hidden" data-testid={`admin-cat-${catKey}`}>
                                    <button onClick={() => setExpandedCat(isExpanded ? null : catKey)}
                                        className="w-full flex items-center justify-between p-3 hover:bg-stone-50 text-left">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-sm font-semibold text-stone-700">{cat.title}</h4>
                                            <span className="text-xs text-stone-400">{Object.values(catAnswers).filter(v => v?.trim()).length}/{cat.questions.length}</span>
                                        </div>
                                        {isExpanded ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                                    </button>
                                    {isExpanded && (
                                        <div className="border-t border-stone-100 divide-y divide-stone-100">
                                            {cat.questions.map((q, idx) => {
                                                const answer = catAnswers[String(idx)] || '';
                                                if (!answer.trim()) return null;
                                                return (
                                                    <div key={idx} className="p-3" data-testid={`admin-answer-${catKey}-${idx}`}>
                                                        <p className="text-xs font-medium text-stone-500 mb-1">{q}</p>
                                                        <p className="text-sm text-stone-800 whitespace-pre-wrap">{answer}</p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div data-testid="voice-profiles-list">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Voice of the User Profiles</h3>
            </div>
            <div className="space-y-2">
                {users.map(u => (
                    <div key={u.user_id} className="flex items-center gap-4 p-3 rounded-lg border border-stone-200 hover:border-stone-300 hover:bg-stone-100/20 transition-colors cursor-pointer"
                        onClick={() => viewProfile(u.user_id)} data-testid={`user-profile-${u.user_id}`}>
                        <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-700 font-bold text-sm flex-shrink-0">{u.name?.charAt(0) || '?'}</div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-stone-800">{u.name}</p>
                                {u.is_admin && <span className="text-xs px-1.5 py-0.5 bg-stone-100 text-stone-700 rounded-full">Admin</span>}
                            </div>
                            <p className="text-xs text-stone-400">{u.email}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                            {u.has_profile ? (
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-24 bg-stone-100 rounded-full h-2 overflow-hidden">
                                            <div className={`h-full rounded-full ${u.completion_pct >= 80 ? 'bg-emerald-500' : u.completion_pct >= 40 ? 'bg-amber-500' : 'bg-stone-1000'}`} style={{ width: `${u.completion_pct}%` }} />
                                        </div>
                                        <span className="text-xs font-medium text-stone-600 w-10 text-right">{u.completion_pct}%</span>
                                    </div>
                                    <p className="text-xs text-stone-400">{u.answered_count} answers</p>
                                </div>
                            ) : (
                                <span className="text-xs text-stone-400">Not started</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


// ─── Email Templates Tab ─────────────────────────────────────────────────────
const EMAIL_TEMPLATE_CATEGORIES = [
    { value: "general", label: "General" },
    { value: "introduction", label: "Introduction" },
    { value: "follow_up", label: "Follow-up" },
    { value: "meeting", label: "Meeting Request" },
    { value: "proposal", label: "Proposal" },
    { value: "check_in", label: "Check-in" },
    { value: "thank_you", label: "Thank You" },
    { value: "cold_outreach", label: "Cold Outreach" },
];

// Batch 7b — 3-tier visibility badge (org=violet, shared=amber, private=stone)
const TEMPLATE_VISIBILITY_BADGE = {
    organization: { label: 'Org-wide', cls: 'bg-violet-100 text-violet-700' },
    shared: { label: 'Shared', cls: 'bg-amber-100 text-amber-700' },
    private: { label: 'Private', cls: 'bg-stone-100 text-stone-600' },
};

function VisibilityBadge({ visibility }) {
    const b = TEMPLATE_VISIBILITY_BADGE[visibility] || TEMPLATE_VISIBILITY_BADGE.private;
    return (
        <span className={`text-xs px-2 py-0.5 rounded font-medium ${b.cls}`}>{b.label}</span>
    );
}

function EmailTemplatesTab() {
    const { getAuthHeader } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form, setForm] = useState({ name: '', subject: '', body: '', category: 'general' });
    const [saving, setSaving] = useState(false);
    const [filterCategory, setFilterCategory] = useState('all');
    // Batch 7b — ownership view
    const [viewMode, setViewMode] = useState('masters');   // 'masters' | 'all'
    const [visibilityFilter, setVisibilityFilter] = useState('all'); // all|private|shared|organization
    const [allTemplates, setAllTemplates] = useState([]);  // org-wide ownership view
    const [allLoading, setAllLoading] = useState(false);
    const [lockingId, setLockingId] = useState(null);

    useEffect(() => { loadTemplates(); }, []);

    const loadTemplates = async () => {
        try {
            const res = await axios.get(`${API}/admin/email-templates`, { headers: getAuthHeader() });
            setTemplates(res.data);
        } catch { toast.error('Failed to load email templates'); }
        finally { setLoading(false); }
    };

    // Ownership view — ALL org templates (private/shared/org) with owner_name.
    const loadAllTemplates = async (vis) => {
        setAllLoading(true);
        try {
            const params = vis && vis !== 'all' ? `?visibility=${encodeURIComponent(vis)}` : '';
            const res = await axios.get(`${API}/admin/email-templates/all${params}`, { headers: getAuthHeader() });
            setAllTemplates(Array.isArray(res.data) ? res.data : []);
        } catch { toast.error('Failed to load org templates'); }
        finally { setAllLoading(false); }
    };

    // Refetch the ownership view whenever it's active or its filter changes.
    useEffect(() => {
        if (viewMode === 'all') loadAllTemplates(visibilityFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, visibilityFilter]);

    // Lock/unlock an org master — locked masters are reference-only (coaches fork to edit).
    const handleToggleLock = async (template) => {
        setLockingId(template.template_id);
        try {
            await axios.put(
                `${API}/admin/email-templates/${template.template_id}`,
                { locked: !template.locked },
                { headers: getAuthHeader() }
            );
            toast.success(template.locked ? 'Template unlocked' : 'Template locked');
            loadTemplates();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update lock state');
        } finally {
            setLockingId(null);
        }
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
            return toast.error('Please provide name, subject, and body');
        }
        setSaving(true);
        try {
            if (editingTemplate) {
                await axios.put(
                    `${API}/admin/email-templates/${editingTemplate.template_id}`,
                    form,
                    { headers: getAuthHeader() }
                );
                toast.success('Template updated');
            } else {
                await axios.post(`${API}/admin/email-templates`, form, { headers: getAuthHeader() });
                toast.success('Template created');
            }
            setShowModal(false);
            setEditingTemplate(null);
            setForm({ name: '', subject: '', body: '', category: 'general' });
            loadTemplates();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (templateId) => {
        if (!window.confirm('Delete this email template?')) return;
        try {
            await axios.delete(`${API}/admin/email-templates/${templateId}`, { headers: getAuthHeader() });
            toast.success('Template deleted');
            loadTemplates();
        } catch { toast.error('Failed to delete template'); }
    };

    const openEdit = (template) => {
        setEditingTemplate(template);
        setForm({
            name: template.name,
            subject: template.subject,
            body: template.body,
            category: template.category || 'general'
        });
        setShowModal(true);
    };

    const openCreate = () => {
        setEditingTemplate(null);
        setForm({ name: '', subject: '', body: '', category: 'general' });
        setShowModal(true);
    };

    const getCategoryLabel = (value) => {
        const cat = EMAIL_TEMPLATE_CATEGORIES.find(c => c.value === value);
        return cat ? cat.label : value;
    };

    const filteredTemplates = filterCategory === 'all'
        ? templates
        : templates.filter(t => t.category === filterCategory);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div data-testid="email-templates-tab">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Email Templates
                </h3>
                <Button onClick={openCreate} className="bg-stone-900 hover:bg-stone-800" data-testid="create-email-template-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Create Template
                </Button>
            </div>
            <p className="text-sm text-stone-500 mb-4">
                Create org master email templates for your sales team. Lock a master to make it
                reference-only — coaches fork it to create their own editable copy.
            </p>

            {/* Batch 7b — view toggle: Org masters vs ownership view */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="inline-flex rounded-lg border border-stone-200 p-0.5 bg-stone-50">
                    <button
                        onClick={() => setViewMode('masters')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            viewMode === 'masters' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                        }`}
                        data-testid="view-org-masters-btn"
                    >
                        Org masters
                    </button>
                    <button
                        onClick={() => setViewMode('all')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            viewMode === 'all' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                        }`}
                        data-testid="view-all-templates-btn"
                    >
                        All org templates
                    </button>
                </div>
                {viewMode === 'all' && (
                    <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
                        <SelectTrigger className="w-40" data-testid="visibility-filter-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All visibilities</SelectItem>
                            <SelectItem value="private">Private</SelectItem>
                            <SelectItem value="shared">Shared</SelectItem>
                            <SelectItem value="organization">Org-wide</SelectItem>
                        </SelectContent>
                    </Select>
                )}
            </div>

            {viewMode === 'masters' ? (
                <>
                    {/* Category Filter (org masters only) */}
                    <div className="flex items-center gap-2 mb-6">
                        <span className="text-sm text-stone-500">Filter:</span>
                        <Select value={filterCategory} onValueChange={setFilterCategory}>
                            <SelectTrigger className="w-40">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Categories</SelectItem>
                                {EMAIL_TEMPLATE_CATEGORIES.map(cat => (
                                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <span className="text-xs text-stone-400 ml-2">
                            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {templates.length === 0 ? (
                        <div className="text-center py-12 border border-dashed border-stone-200 rounded-xl">
                            <Mail className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                            <p className="text-stone-500 mb-3">No email templates yet</p>
                            <Button variant="outline" onClick={openCreate}>
                                <Plus className="w-4 h-4 mr-1" /> Create First Template
                            </Button>
                        </div>
                    ) : filteredTemplates.length === 0 ? (
                        <div className="text-center py-12 border border-dashed border-stone-200 rounded-xl">
                            <p className="text-stone-500">No templates in this category</p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {filteredTemplates.map(template => (
                                <div
                                    key={template.template_id}
                                    className="border border-stone-200 rounded-xl p-4 hover:border-stone-300 transition-colors"
                                    data-testid={`email-template-${template.template_id}`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="font-semibold text-stone-800">{template.name}</h4>
                                                <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded">
                                                    {getCategoryLabel(template.category)}
                                                </span>
                                                <VisibilityBadge visibility={template.visibility || 'organization'} />
                                                {template.locked && (
                                                    <span className="inline-flex items-center gap-1 text-xs bg-stone-800 text-white px-2 py-0.5 rounded">
                                                        <Lock className="w-3 h-3" /> Locked
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-stone-600 font-medium">
                                                Subject: {template.subject}
                                            </p>
                                            <p className="text-xs text-stone-400 mt-1">
                                                Created by {template.owner_name || template.created_by_name || 'Admin'}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7"
                                                disabled={lockingId === template.template_id}
                                                onClick={() => handleToggleLock(template)}
                                                title={template.locked ? 'Unlock — allow editing' : 'Lock — coaches fork to edit'}
                                                data-testid={`lock-toggle-${template.template_id}`}
                                            >
                                                {template.locked
                                                    ? <Unlock className="w-3 h-3 mr-1" />
                                                    : <Lock className="w-3 h-3 mr-1" />}
                                                {template.locked ? 'Unlock' : 'Lock'}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7"
                                                onClick={() => openEdit(template)}
                                            >
                                                <Edit3 className="w-3 h-3 mr-1" /> Edit
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                onClick={() => handleDelete(template.template_id)}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    </div>
                                    <div
                                        className="text-sm text-stone-500 prose prose-sm max-w-none line-clamp-2 border-t border-stone-100 pt-2 mt-2"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.body) }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                /* Batch 7b — read-only ownership view across ALL org templates */
                <div>
                    {allLoading ? (
                        <div className="flex items-center justify-center h-40">
                            <div className="w-6 h-6 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : allTemplates.length === 0 ? (
                        <div className="text-center py-12 border border-dashed border-stone-200 rounded-xl">
                            <p className="text-stone-500">No templates match this filter</p>
                        </div>
                    ) : (
                        <div className="border border-stone-200 rounded-xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-stone-50 border-b border-stone-200">
                                    <tr className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">
                                        <th className="px-4 py-2">Template</th>
                                        <th className="px-4 py-2">Owner</th>
                                        <th className="px-4 py-2">Category</th>
                                        <th className="px-4 py-2">Visibility</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allTemplates.map(template => (
                                        <tr key={template.template_id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60">
                                            <td className="px-4 py-2.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-stone-800">{template.name}</span>
                                                    {template.locked && <Lock className="w-3 h-3 text-stone-500" />}
                                                </div>
                                                <p className="text-xs text-stone-400 truncate max-w-md">{template.subject}</p>
                                            </td>
                                            <td className="px-4 py-2.5 text-stone-600">{template.owner_name || '—'}</td>
                                            <td className="px-4 py-2.5 text-stone-500">{getCategoryLabel(template.category)}</td>
                                            <td className="px-4 py-2.5"><VisibilityBadge visibility={template.visibility || 'private'} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <p className="text-xs text-stone-400 mt-3">
                        Read-only ownership view — shows every template across the org without exposing each coach's content.
                    </p>
                </div>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
                        </DialogTitle>
                        <DialogDescription>
                            Create reusable email templates for your sales team.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Template Name</Label>
                                <Input
                                    value={form.name}
                                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                    placeholder="e.g., Initial Outreach, Follow-up #1..."
                                    className="mt-1"
                                    data-testid="email-template-name-input"
                                />
                            </div>
                            <div>
                                <Label>Category</Label>
                                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                                    <SelectTrigger className="mt-1" data-testid="email-template-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {EMAIL_TEMPLATE_CATEGORIES.map(cat => (
                                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Subject Line</Label>
                            <Input
                                value={form.subject}
                                onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                                placeholder="e.g., Quick question about {{Company}}..."
                                className="mt-1"
                                data-testid="email-template-subject-input"
                            />
                            <p className="text-xs text-stone-400 mt-1">
                                Use {'{{FirstName}}'}, {'{{Company}}'}, {'{{Title}}'} for personalization
                            </p>
                        </div>
                        <div>
                            <Label>Email Body</Label>
                            <div className="mt-1" data-testid="email-template-body-editor">
                                <RichTextEditor
                                    content={form.body}
                                    onChange={(html) => setForm(p => ({ ...p, body: html }))}
                                    placeholder="Write your email template here..."
                                    minHeight="200px"
                                    showMergeVariables={true}
                                    showAIWriter={true}
                                    showContentLibrary={true}
                                    onSubjectGenerated={(subj) => setForm(p => ({ ...p, subject: subj }))}
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-stone-900 hover:bg-stone-800"
                            data-testid="save-email-template-btn"
                        >
                            {saving ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


// ─── Signature Templates Tab ───────────────────────────────────────────────
function SignatureTemplatesTab() {
    const { getAuthHeader } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form, setForm] = useState({ name: '', content: '' });
    const [signatureData, setSignatureData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeOverrideId, setActiveOverrideId] = useState(null);

    useEffect(() => { loadTemplates(); }, []);

    const loadTemplates = async () => {
        try {
            const res = await axios.get(`${API}/admin/signatures`, { headers: getAuthHeader() });
            if (res.data.active_override_id !== undefined) {
                setActiveOverrideId(res.data.active_override_id);
                setTemplates(res.data.templates || []);
            } else {
                setTemplates(Array.isArray(res.data) ? res.data : []);
            }
        } catch { toast.error('Failed to load signature templates'); }
        finally { setLoading(false); }
    };

    const handleSetOverride = async (signatureId) => {
        try {
            await axios.post(`${API}/admin/signatures/${signatureId}/set-override`, {}, { headers: getAuthHeader() });
            setActiveOverrideId(signatureId);
            toast.success('Signature override set — all users will now use this signature template.');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to set override');
        }
    };

    const handleClearOverride = async () => {
        try {
            await axios.delete(`${API}/admin/signatures/override`, { headers: getAuthHeader() });
            setActiveOverrideId(null);
            toast.success('Override removed — users will use their personal signatures.');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to clear override');
        }
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            return toast.error('Please provide a template name');
        }
        // If content is empty but signatureData exists, call the render API directly
        let content = form.content;
        if (!content?.trim() && signatureData) {
            try {
                const res = await axios.post(`${API}/admin/signatures/render-html`, signatureData, { headers: getAuthHeader() });
                content = res.data?.html || '';
            } catch {
                // Fallback: build minimal HTML client-side from signatureData fields
                const f = signatureData.fields || {};
                const parts = [f.name, f.title, f.company, f.email].filter(Boolean);
                if (parts.length) {
                    content = `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,sans-serif"><tr><td><strong>${f.name || ''}</strong></td></tr>${f.title || f.company ? `<tr><td>${[f.title, f.company].filter(Boolean).join(' · ')}</td></tr>` : ''}${f.email ? `<tr><td><a href="mailto:${f.email}">${f.email}</a></td></tr>` : ''}</table>`;
                }
            }
        }
        if (!content?.trim()) {
            return toast.error('Signature content is empty — fill in at least a name or title');
        }
        setSaving(true);
        try {
            const payload = { name: form.name, content };
            if (signatureData) payload.signature_data = signatureData;
            if (editingTemplate) {
                await axios.put(
                    `${API}/admin/signatures/${editingTemplate.signature_id}`,
                    payload,
                    { headers: getAuthHeader() }
                );
                toast.success('Template updated');
            } else {
                await axios.post(`${API}/admin/signatures`, payload, { headers: getAuthHeader() });
                toast.success('Template created');
            }
            setShowModal(false);
            setEditingTemplate(null);
            setForm({ name: '', content: '' });
            setSignatureData(null);
            loadTemplates();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (sigId) => {
        if (!window.confirm('Delete this signature template?')) return;
        try {
            await axios.delete(`${API}/admin/signatures/${sigId}`, { headers: getAuthHeader() });
            toast.success('Template deleted');
            loadTemplates();
        } catch { toast.error('Failed to delete template'); }
    };

    const openEdit = (template) => {
        setEditingTemplate(template);
        setForm({ name: template.name, content: template.content });
        setSignatureData(template.signature_data || null);
        setShowModal(true);
    };

    const openCreate = () => {
        setEditingTemplate(null);
        setForm({ name: '', content: '' });
        setSignatureData(null);
        setShowModal(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div data-testid="signature-templates-tab">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Email Signature Templates
                </h3>
                <Button onClick={openCreate} className="bg-stone-900 hover:bg-stone-800" data-testid="create-signature-template-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Create Template
                </Button>
            </div>
            <p className="text-sm text-stone-500 mb-6">
                Create signature templates that all users can access and customize for their emails.
            </p>

            {activeOverrideId && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-amber-600" />
                        <span className="text-sm text-amber-800">
                            <strong>Org Override Active</strong> — All users are using the override signature template
                        </span>
                    </div>
                    <button onClick={handleClearOverride} className="text-xs text-amber-700 hover:text-amber-900 underline">
                        Remove Override
                    </button>
                </div>
            )}

            {templates.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-200 rounded-xl">
                    <FileText className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                    <p className="text-stone-500 mb-3">No signature templates yet</p>
                    <Button variant="outline" onClick={openCreate}>
                        <Plus className="w-4 h-4 mr-1" /> Create First Template
                    </Button>
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                    {templates.map(template => (
                        <div
                            key={template.signature_id}
                            className="border border-stone-200 rounded-xl p-4 hover:border-stone-300 transition-colors"
                            data-testid={`signature-template-${template.signature_id}`}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h4 className="font-semibold text-stone-800">{template.name}</h4>
                                    <p className="text-xs text-stone-400 mt-0.5">
                                        Created by {template.created_by_name || 'Admin'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1">
                                    {activeOverrideId === template.signature_id ? (
                                        <>
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                                                <Shield className="w-3 h-3" /> Active Override
                                            </span>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                                                onClick={handleClearOverride}
                                            >
                                                <X className="w-3 h-3 mr-1" /> Remove
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                            onClick={() => handleSetOverride(template.signature_id)}
                                        >
                                            <Shield className="w-3 h-3 mr-1" /> Set Override
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7"
                                        onClick={() => openEdit(template)}
                                    >
                                        <Edit3 className="w-3 h-3 mr-1" /> Edit
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                                        onClick={() => handleDelete(template.signature_id)}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            </div>
                            <div
                                className="text-sm text-stone-600 prose prose-sm max-w-none line-clamp-3 border-t border-stone-100 pt-3"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.content) }}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingTemplate ? 'Edit Signature Template' : 'Create Signature Template'}
                        </DialogTitle>
                        <DialogDescription>
                            Design a signature template with side-by-side photo and text layouts. Users can copy and customize it for their emails.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label>Template Name</Label>
                            <Input
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="e.g., Standard Company Signature, Sales Team Signature..."
                                className="mt-1"
                                data-testid="admin-signature-name-input"
                            />
                        </div>
                        <div data-testid="admin-signature-content-editor">
                            <SignatureEditor
                                value={form.content}
                                onChange={(html) => setForm(p => ({ ...p, content: html }))}
                                signatureData={signatureData}
                                onDataChange={setSignatureData}
                                isAdmin={true}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-stone-900 hover:bg-stone-800"
                            data-testid="admin-save-signature-btn"
                        >
                            {saving ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


/* ── App Launcher Tab ────────────────────────────────────────────────────── */
const APP_COLORS = ['#4f46e5','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777','#475569'];

function AppLauncherTab() {
    const { getAuthHeader } = useAuth();
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [form, setForm] = useState({ name: '', url: '', icon_url: '', color: APP_COLORS[0] });

    const fetchLinks = async () => {
        try {
            const res = await axios.get(`${API}/organizations/current`, { headers: getAuthHeader() });
            setLinks(res.data?.settings?.app_links || []);
        } catch { toast.error('Failed to load app links'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchLinks(); }, []);

    const saveLinks = async (newLinks) => {
        setSaving(true);
        try {
            await axios.put(`${API}/organizations/current`, {
                settings: { app_links: newLinks },
            }, { headers: getAuthHeader() });
            setLinks(newLinks);
            toast.success('App links saved');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const openAdd = () => {
        setEditIndex(null);
        setForm({ name: '', url: '', icon_url: '', color: APP_COLORS[0] });
        setShowDialog(true);
    };

    const openEdit = (idx) => {
        setEditIndex(idx);
        setForm({ ...links[idx] });
        setShowDialog(true);
    };

    const handleSave = () => {
        if (!form.name.trim() || !form.url.trim()) { toast.error('Name and URL are required'); return; }
        let url = form.url.trim();
        if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
        const entry = {
            id: editIndex !== null ? links[editIndex].id : crypto.randomUUID(),
            name: form.name.trim(),
            url,
            icon_url: form.icon_url.trim() || '',
            color: form.color || APP_COLORS[0],
            order: editIndex !== null ? links[editIndex].order : links.length,
        };
        const newLinks = editIndex !== null
            ? links.map((l, i) => i === editIndex ? entry : l)
            : [...links, entry];
        saveLinks(newLinks);
        setShowDialog(false);
    };

    const handleDelete = (idx) => {
        const newLinks = links.filter((_, i) => i !== idx).map((l, i) => ({ ...l, order: i }));
        saveLinks(newLinks);
    };

    const moveLink = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= links.length) return;
        const newLinks = [...links];
        [newLinks[idx], newLinks[target]] = [newLinks[target], newLinks[idx]];
        saveLinks(newLinks.map((l, i) => ({ ...l, order: i })));
    };

    const pickColor = (name, color) => {
        if (color) return color;
        let hash = 0;
        for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        return APP_COLORS[Math.abs(hash) % APP_COLORS.length];
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>App Launcher</h3>
                    <p className="text-sm text-stone-500">Configure quick-access links that appear in the waffle menu for all team members.</p>
                </div>
                <Button onClick={openAdd} size="sm"><Plus className="w-4 h-4 mr-1" /> Add App</Button>
            </div>

            {links.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-stone-400 border border-dashed border-stone-300 rounded-xl">
                    <LayoutGrid className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm font-medium text-stone-500">No apps configured</p>
                    <p className="text-xs mt-1">Add external tools and websites your team uses daily.</p>
                </div>
            ) : (
                <div className="border border-stone-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 text-stone-500 text-xs">
                            <tr>
                                <th className="text-left px-4 py-2.5 font-medium">App</th>
                                <th className="text-left px-4 py-2.5 font-medium">URL</th>
                                <th className="text-right px-4 py-2.5 font-medium w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {links.map((link, idx) => (
                                <tr key={link.id || idx} className="hover:bg-stone-50">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            {link.icon_url ? (
                                                <img src={link.icon_url} alt="" className="w-8 h-8 rounded-lg object-cover bg-stone-100"
                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                            ) : null}
                                            <div
                                                className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                                                style={{
                                                    backgroundColor: pickColor(link.name, link.color),
                                                    display: link.icon_url ? 'none' : 'flex',
                                                }}
                                            >
                                                {(link.name || '?').charAt(0).toUpperCase()}
                                            </div>
                                            <span className="font-medium text-stone-900">{link.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-stone-500 hover:text-blue-600 truncate block max-w-xs">
                                            {link.url}
                                        </a>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => moveLink(idx, -1)} disabled={idx === 0}
                                                className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700 disabled:opacity-30">
                                                <ArrowUp className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => moveLink(idx, 1)} disabled={idx === links.length - 1}
                                                className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700 disabled:opacity-30">
                                                <ArrowDown className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => openEdit(idx)}
                                                className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700">
                                                <Edit3 className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => handleDelete(idx)}
                                                className="p-1 rounded hover:bg-red-100 text-stone-400 hover:text-red-600">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Live Preview */}
            {links.length > 0 && (
                <div>
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Preview</h4>
                    <div className="inline-flex bg-white border border-stone-200 rounded-xl p-3 shadow-sm">
                        <div className="grid grid-cols-3 gap-1">
                            {links.map((link) => (
                                <div key={link.id || link.name} className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg w-20">
                                    {link.icon_url ? (
                                        <img src={link.icon_url} alt={link.name} className="w-10 h-10 rounded-xl object-cover bg-stone-100" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base"
                                            style={{ backgroundColor: pickColor(link.name, link.color) }}>
                                            {(link.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <span className="text-[11px] text-stone-600 font-medium text-center leading-tight truncate w-full">{link.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editIndex !== null ? 'Edit App' : 'Add App'}</DialogTitle>
                        <DialogDescription>Configure an external app link for the launcher menu.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Name *</Label>
                            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Slack" />
                        </div>
                        <div>
                            <Label>URL *</Label>
                            <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="e.g. https://slack.com" />
                        </div>
                        <div>
                            <Label>Icon URL (optional)</Label>
                            <Input value={form.icon_url} onChange={e => setForm(f => ({ ...f, icon_url: e.target.value }))} placeholder="e.g. https://example.com/icon.png" />
                            <p className="text-[11px] text-stone-400 mt-1">Paste a favicon or logo URL. Leave blank for a letter icon.</p>
                        </div>
                        <div>
                            <Label>Color</Label>
                            <div className="flex items-center gap-2 mt-1">
                                {APP_COLORS.map(c => (
                                    <button
                                        key={c}
                                        onClick={() => setForm(f => ({ ...f, color: c }))}
                                        className={`w-7 h-7 rounded-lg transition-all ${form.color === c ? 'ring-2 ring-offset-2 ring-stone-900 scale-110' : 'hover:scale-105'}`}
                                        style={{ backgroundColor: c }}
                                    />
                                ))}
                            </div>
                            <p className="text-[11px] text-stone-400 mt-1">Used as fallback when no icon URL is set.</p>
                        </div>
                        {/* Preview */}
                        <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                            {form.icon_url ? (
                                <img src={form.icon_url} alt="" className="w-10 h-10 rounded-xl object-cover bg-stone-100" />
                            ) : (
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base"
                                    style={{ backgroundColor: form.color || APP_COLORS[0] }}>
                                    {(form.name || '?').charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div>
                                <p className="text-sm font-medium text-stone-900">{form.name || 'App Name'}</p>
                                <p className="text-xs text-stone-400">{form.url || 'https://...'}</p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : (editIndex !== null ? 'Update' : 'Add App')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ─── Pending Approvals Tab ──────────────────────────────────────────────────
function PendingApprovalsTab() {
    const { getAuthHeader } = useAuth();
    const [pendingUsers, setPendingUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(null);
    const [showApproveDialog, setShowApproveDialog] = useState(null);
    const [approveRole, setApproveRole] = useState('sales_rep');
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        loadPending();
    }, []);

    const loadPending = async () => {
        try {
            const res = await axios.get(`${API}/admin/pending-users`, { headers: getAuthHeader() });
            setPendingUsers(res.data || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };

    const handleApprove = async (userId) => {
        setApproving(userId);
        try {
            await axios.put(`${API}/admin/users/${userId}/approve`, { role: approveRole }, { headers: getAuthHeader() });
            toast.success('User approved');
            setShowApproveDialog(null);
            loadPending();
        } catch { toast.error('Failed to approve user'); }
        finally { setApproving(null); }
    };

    const handleReject = async (userId) => {
        if (!window.confirm('Are you sure you want to reject this user?')) return;
        setApproving(userId);
        try {
            await axios.put(`${API}/admin/users/${userId}/reject`, {}, { headers: getAuthHeader() });
            toast.success('User rejected');
            loadPending();
        } catch { toast.error('Failed to reject user'); }
        finally { setApproving(null); }
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>;

    if (pendingUsers.length === 0) {
        return (
            <div className="text-center py-16">
                <UserCheck className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">No pending approvals</p>
                <p className="text-xs text-stone-400 mt-1">Users who register with your organization's email domain will appear here for approval.</p>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-amber-600" />
                <h3 className="text-sm font-semibold text-stone-700">{pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} waiting for approval</h3>
            </div>
            <div className="space-y-3">
                {pendingUsers.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between p-4 bg-amber-50/50 border border-amber-200/60 rounded-xl">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-sm font-bold text-amber-700">
                                {(u.name || u.email || '?').charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <p className="text-sm font-medium text-stone-900">{u.name || 'Unknown'}</p>
                                <p className="text-xs text-stone-500">{u.email}</p>
                                <p className="text-xs text-stone-400 mt-0.5">
                                    Registered {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'recently'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {showApproveDialog === u.user_id ? (
                                <div className="flex items-center gap-2">
                                    <select value={approveRole} onChange={e => setApproveRole(e.target.value)}
                                        className="px-2 py-1.5 text-xs border border-stone-200 rounded-lg bg-white">
                                        <option value="sales_rep">Sales Rep</option>
                                        <option value="manager">Manager</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                    <button onClick={() => handleApprove(u.user_id)} disabled={approving === u.user_id}
                                        className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                                        {approving === u.user_id ? '...' : 'Confirm'}
                                    </button>
                                    <button onClick={() => setShowApproveDialog(null)}
                                        className="px-2 py-1.5 text-xs text-stone-500 hover:text-stone-700">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <button onClick={() => { setApproveRole('sales_rep'); setShowApproveDialog(u.user_id); }}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                                        <UserCheck className="w-3.5 h-3.5" /> Approve
                                    </button>
                                    <button onClick={() => handleReject(u.user_id)} disabled={approving === u.user_id}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50">
                                        <UserX className="w-3.5 h-3.5" /> Reject
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


function AIUsageTab() {
    const { getAuthHeader } = useAuth();
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30');

    useEffect(() => {
        loadUsage();
    }, [period]);

    const loadUsage = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/usage/ai?days=${period}`, { headers: getAuthHeader() });
            setUsage(res.data);
        } catch { toast.error('Failed to load AI usage data'); }
        finally { setLoading(false); }
    };

    if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-stone-400" /></div>;

    const totals = usage?.totals || {};
    const byDay = usage?.by_day || [];
    const byUser = usage?.by_user || [];
    const byFeature = usage?.by_feature || [];

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Usage & Costs</h3>
                    <p className="text-xs text-stone-500 mt-0.5">Monitor your organization's AI token consumption and costs</p>
                </div>
                <select
                    value={period}
                    onChange={e => setPeriod(e.target.value)}
                    className="px-3 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none"
                >
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                </select>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-stone-50 rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-violet-500" />
                        <span className="text-xs text-stone-500 font-medium">Total Tokens</span>
                    </div>
                    <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {(totals.total_tokens || 0).toLocaleString()}
                    </p>
                </div>
                <div className="bg-stone-50 rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-stone-500 font-medium">Total Cost</span>
                    </div>
                    <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        ${(totals.total_cost || 0).toFixed(4)}
                    </p>
                </div>
                <div className="bg-stone-50 rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        <span className="text-xs text-stone-500 font-medium">API Calls</span>
                    </div>
                    <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {(totals.total_calls || byDay.reduce((sum, d) => sum + 1, 0)).toLocaleString()}
                    </p>
                </div>
            </div>

            {/* Cost info box */}
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                <h4 className="text-xs font-semibold text-violet-800 mb-1">Cost Breakdown (GPT-4o)</h4>
                <p className="text-[10px] text-violet-600 leading-relaxed">
                    Input: $2.50/1M tokens | Output: $10.00/1M tokens | GPT-4o-mini: $0.15/$0.60 per 1M tokens.
                    Costs are tracked per API call and include AI chat, email writing, deal analysis, and agentic personalization.
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* By Feature */}
                <div>
                    <h4 className="text-sm font-semibold text-stone-900 mb-3">Usage by Feature</h4>
                    <div className="space-y-2">
                        {byFeature.length === 0 ? (
                            <p className="text-xs text-stone-400 py-4 text-center">No usage data yet</p>
                        ) : byFeature.map((f, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
                                <span className="text-xs font-medium text-stone-700">{(f.feature || 'unknown').replace(/_/g, ' ')}</span>
                                <div className="text-right">
                                    <span className="text-xs font-semibold text-stone-900">{(f.tokens || 0).toLocaleString()} tokens</span>
                                    <span className="text-[10px] text-stone-400 ml-2">${(f.cost || 0).toFixed(4)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* By User */}
                <div>
                    <h4 className="text-sm font-semibold text-stone-900 mb-3">Top Users</h4>
                    <div className="space-y-2">
                        {byUser.length === 0 ? (
                            <p className="text-xs text-stone-400 py-4 text-center">No usage data yet</p>
                        ) : byUser.slice(0, 10).map((u, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
                                <span className="text-xs font-medium text-stone-700 truncate max-w-[150px]">{u._id || 'Unknown'}</span>
                                <div className="text-right">
                                    <span className="text-xs font-semibold text-stone-900">{(u.tokens || 0).toLocaleString()} tokens</span>
                                    <span className="text-[10px] text-stone-400 ml-2">${(u.cost || 0).toFixed(4)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Daily Trend */}
            {byDay.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold text-stone-900 mb-3">Daily Usage</h4>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-stone-200">
                                    <th className="text-left py-2 px-3 text-stone-500 font-medium">Date</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">Tokens</th>
                                    <th className="text-right py-2 px-3 text-stone-500 font-medium">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byDay.slice(-14).reverse().map((d, i) => (
                                    <tr key={i} className="border-b border-stone-100">
                                        <td className="py-2 px-3 text-stone-700">{d.date}</td>
                                        <td className="py-2 px-3 text-right text-stone-900 font-medium">{(d.tokens || 0).toLocaleString()}</td>
                                        <td className="py-2 px-3 text-right text-stone-500">${(d.cost || 0).toFixed(4)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}


function ProductSettingsTab() {
    const { getAuthHeader } = useAuth();
    const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const [categories, setCategories] = useState([]);
    const [pricingModels, setPricingModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newCat, setNewCat] = useState({ value: '', label: '', sku_prefix: '' });
    const [newPM, setNewPM] = useState({ value: '', label: '' });

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/products/settings`, { headers: getAuthHeader() });
                setCategories(res.data.categories || []);
                setPricingModels(res.data.pricing_models || []);
            } catch { toast.error('Failed to load product settings'); }
            finally { setLoading(false); }
        })();
    }, []);

    const save = async (cats, pms) => {
        setSaving(true);
        try {
            await axios.put(`${API_BASE}/products/settings`, {
                categories: cats, pricing_models: pms,
            }, { headers: getAuthHeader() });
            toast.success('Product settings saved');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const addCategory = () => {
        if (!newCat.label.trim()) return;
        const value = newCat.value.trim() || newCat.label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        const sku_prefix = (newCat.sku_prefix.trim() || newCat.label.trim().substring(0, 3)).toUpperCase().replace(/[^A-Z0-9]/g, '');
        const updated = [...categories, { value, label: newCat.label.trim(), sku_prefix }];
        setCategories(updated);
        setNewCat({ value: '', label: '', sku_prefix: '' });
        save(updated, pricingModels);
    };

    const updateCategoryPrefix = (idx, prefix) => {
        const updated = categories.map((cat, i) => i === idx ? { ...cat, sku_prefix: prefix.toUpperCase().replace(/[^A-Z0-9]/g, '') } : cat);
        setCategories(updated);
        save(updated, pricingModels);
    };

    const removeCategory = (idx) => {
        const updated = categories.filter((_, i) => i !== idx);
        setCategories(updated);
        save(updated, pricingModels);
    };

    const addPricingModel = () => {
        if (!newPM.label.trim()) return;
        const value = newPM.value.trim() || newPM.label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        const updated = [...pricingModels, { value, label: newPM.label.trim() }];
        setPricingModels(updated);
        setNewPM({ value: '', label: '' });
        save(categories, updated);
    };

    const removePricingModel = (idx) => {
        const updated = pricingModels.filter((_, i) => i !== idx);
        setPricingModels(updated);
        save(categories, updated);
    };

    if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>;

    return (
        <div className="space-y-8 max-w-2xl">
            <div>
                <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Product Settings</h3>
                <p className="text-sm text-stone-500 mt-1">Configure categories and pricing models that appear in the product editor.</p>
            </div>

            {/* Categories */}
            <div>
                <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Product Categories</Label>
                <p className="text-xs text-stone-400 mt-0.5 mb-3">These appear as filter options and category dropdowns when creating products.</p>
                <div className="space-y-1.5 mb-3">
                    {categories.map((cat, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-stone-50 border border-stone-100 rounded-lg">
                            <span className="text-sm font-medium text-stone-700 flex-1">{cat.label}</span>
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-stone-400">SKU prefix:</span>
                                <input
                                    value={cat.sku_prefix || ''}
                                    onChange={e => updateCategoryPrefix(idx, e.target.value)}
                                    placeholder={cat.label.substring(0, 3).toUpperCase()}
                                    className="w-16 px-1.5 py-0.5 text-xs font-mono text-stone-700 bg-white border border-stone-200 rounded text-center uppercase"
                                    maxLength={6}
                                />
                            </div>
                            <button onClick={() => removeCategory(idx)} className="p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <Input
                        value={newCat.label}
                        onChange={e => setNewCat({ ...newCat, label: e.target.value })}
                        placeholder="Category name (e.g., Professional Services)"
                        className="flex-1 text-sm"
                        onKeyDown={e => e.key === 'Enter' && addCategory()}
                    />
                    <Input
                        value={newCat.sku_prefix}
                        onChange={e => setNewCat({ ...newCat, sku_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                        placeholder="SKU (e.g., WKS)"
                        className="w-24 text-sm font-mono uppercase text-center"
                        maxLength={6}
                    />
                    <Button onClick={addCategory} variant="outline" size="sm" disabled={!newCat.label.trim()}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                </div>
            </div>

            {/* Pricing Models */}
            <div>
                <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Pricing Models</Label>
                <p className="text-xs text-stone-400 mt-0.5 mb-3">Define the pricing structures available for your products.</p>
                <div className="space-y-1.5 mb-3">
                    {pricingModels.map((pm, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-stone-50 border border-stone-100 rounded-lg">
                            <span className="text-sm font-medium text-stone-700 flex-1">{pm.label}</span>
                            <span className="text-xs text-stone-400 font-mono">{pm.value}</span>
                            <button onClick={() => removePricingModel(idx)} className="p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <Input
                        value={newPM.label}
                        onChange={e => setNewPM({ ...newPM, label: e.target.value })}
                        placeholder="Pricing model name (e.g., Subscription)"
                        className="flex-1 text-sm"
                        onKeyDown={e => e.key === 'Enter' && addPricingModel()}
                    />
                    <Button onClick={addPricingModel} variant="outline" size="sm" disabled={!newPM.label.trim()}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                </div>
            </div>
        </div>
    );
}


export default function Admin() {
    const [activeTab, setActiveTab] = useState('team');
    const [pendingCount, setPendingCount] = useState(0);
    const { getAuthHeader } = useAuth();
    const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

    // Load pending count for badge
    useEffect(() => {
        const loadPendingCount = async () => {
            try {
                const res = await axios.get(`${API_BASE}/admin/pending-users`, { headers: getAuthHeader() });
                setPendingCount((res.data || []).length);
            } catch { /* ignore */ }
        };
        loadPendingCount();
    }, [activeTab, getAuthHeader]);

    const tabs = [
        { key: 'pending', label: 'Pending Approvals', icon: Clock, badge: pendingCount },
        { key: 'team', label: 'Team Members', icon: UserCog },
        { key: 'coaches', label: 'Coaches', icon: Presentation },
        { key: 'groups', label: 'User Groups', icon: Users },
        { key: 'templates', label: 'Deal Room Templates', icon: Layers },
        { key: 'email-templates', label: 'Email Templates', icon: Mail },
        { key: 'signatures', label: 'Signatures', icon: FileSignature },
        { key: 'skills', label: 'AI Skills', icon: Sparkles },
        { key: 'media', label: 'Media Library', icon: Image },
        { key: 'voice', label: 'Voice Profiles', icon: Mic },
        { key: 'tags', label: 'Tags', icon: Tag },
        { key: 'custom-fields', label: 'Custom Fields', icon: Layers },
        { key: 'lead-pipeline', label: 'Lead Pipeline', icon: Target },
        { key: 'event-types', label: 'Event Types', icon: CalendarDays },
        { key: 'product-settings', label: 'Product Settings', icon: Package },
        { key: 'app-launcher', label: 'App Launcher', icon: LayoutGrid },
        { key: 'ai-usage', label: 'AI Usage', icon: TrendingUp },
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin</h1>
                <p className="text-stone-500 text-sm mt-1">Manage your team, templates, groups, and shared media</p>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="border-b border-stone-200 px-4 overflow-x-auto">
                    <div className="flex gap-0 min-w-max">
                        {tabs.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                    className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-stone-700 text-stone-700' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
                                    data-testid={`admin-tab-${tab.key}`}>
                                    <Icon className="w-4 h-4" />{tab.label}
                                    {tab.badge > 0 && (
                                        <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full leading-none">
                                            {tab.badge}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="p-6">
                    {activeTab === 'pending' && <PendingApprovalsTab />}
                    {activeTab === 'team' && <TeamMembersTab />}
                    {activeTab === 'coaches' && <CoachesTab />}
                    {activeTab === 'groups' && <UserGroupsTab />}
                    {activeTab === 'templates' && <TemplatesTab />}
                    {activeTab === 'email-templates' && <EmailTemplatesTab />}
                    {activeTab === 'signatures' && <SignatureTemplatesTab />}
                    {activeTab === 'skills' && <AISkillsTab />}
                    {activeTab === 'media' && <MediaLibraryTab />}
                    {activeTab === 'voice' && <VoiceProfilesTab />}
                    {activeTab === 'tags' && <TagManagementTab />}
                    {activeTab === 'custom-fields' && <CustomFieldsTab />}
                    {activeTab === 'lead-pipeline' && <LeadPipelineTab />}
                    {activeTab === 'event-types' && <EventTypeAdmin />}
                    {activeTab === 'product-settings' && <ProductSettingsTab />}
                    {activeTab === 'app-launcher' && <AppLauncherTab />}
                    {activeTab === 'ai-usage' && <AIUsageTab />}
                </div>
            </div>
        </div>
    );
}
